import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api, resolveAsset, authHeader } from '../../api/client';
import { buildDedication, buildDedicationText, buildSummary, CoverTitle, splitTitleLines, COVER_FONTS, sceneTextSizeClass } from '../../components/BookViewer';
import TextBubble from '../../lib/textBubble';
import { storyTextDir } from '../../lib/bidi';
import { normScenePos } from '../../lib/sceneText';
import { STORY_FONTS, storyFontCss } from '../../lib/storyFonts';
import { useT } from '../../i18n';
import DuplicateStoryModal from '../../components/DuplicateStoryModal';

// Append a cache-busting version to an asset URL so a regenerated/uploaded image
// that reuses the same URL still reloads in the browser.
function bust(url, savedAt) {
  if (!url) return null;
  const resolved = resolveAsset(url);
  if (!savedAt) return resolved;
  return `${resolved}${url.includes('?') ? '&' : '?'}v=${savedAt}`;
}

// Which Seedream backend painted an image: kie.ai (the primary, cheaper) or
// fal.ai (the fallback). The server routes between the two on its own — long
// prompts and every cover go to fal — so this is the only way to see, per image,
// where the money actually went. Null on legacy images and on non-Seedream
// providers, where nothing is shown at all.
const BACKEND_LABELS = {
  kie: { text: 'kie.ai', title: 'وُلِّدت عبر kie.ai (المزوّد الأساسي، الأرخص)' },
  fal: { text: 'fal.ai', title: 'وُلِّدت عبر fal.ai (المزوّد الاحتياطي، الأغلى)' },
};

function BackendBadge({ backend }) {
  const label = BACKEND_LABELS[backend];
  if (!label) return null;
  return (
    <span className={`admin-backend-badge admin-backend-badge--${backend}`} title={label.title}>
      {label.text}
    </span>
  );
}

// Story-level tally of the two backends across the cover + every illustrated
// scene, so "kie, fal, or both" is answerable at a glance without counting
// badges. Returns null when no image records a backend (legacy stories).
function BackendSummary({ story }) {
  const backends = [
    story?.coverImageBackend,
    ...(story?.scenes || []).map((s) => s.imageBackend),
  ].filter((b) => BACKEND_LABELS[b]);
  if (!backends.length) return null;
  const kie = backends.filter((b) => b === 'kie').length;
  const fal = backends.filter((b) => b === 'fal').length;
  const mix = kie && fal ? 'kie + fal' : kie ? 'kie فقط' : 'fal فقط';
  return (
    <p className="admin-scene-meta admin-backend-summary">
      مزوّد توليد الصور: <strong>{mix}</strong>
      {' — '}
      <BackendBadge backend="kie" /> {kie}
      {'  '}
      <BackendBadge backend="fal" /> {fal}
      {' من '}
      {backends.length} صورة
    </p>
  );
}

// Download ONE print-house file: part='cover' (the wraparound case wrap) or
// part='pages' (the interior, page-by-page — text→image per scene, 2mm bleed,
// dedication first). fetch→blob→click a hidden <a download> so it actually saves
// (a GCS 302 would otherwise open the bytes inline). Throws so the caller can
// surface the error.
async function downloadPrintPart(shareToken, safeBase, part, barcode) {
  // Name the file by the tracking barcode only — ASCII so the print house's file
  // system never mangles it: e.g. "cover_AB-19042.pdf" / "story_AB-19042.pdf". An
  // Arabic title (safeBase) is deliberately not used. Fall back to the share
  // token when no barcode is set yet so the file is never nameless.
  const bc = (barcode || '').trim() || shareToken;
  const prefix = part === 'cover' ? 'cover' : 'story';
  const res = await fetch(resolveAsset(`/api/stories/${shareToken}/book-print.pdf?part=${part}`), { headers: authHeader() });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { const d = await res.json(); if (d?.error) msg = d.error; } catch {}
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${prefix}_${bc}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Download the finished book PDF as an admin. Unlike the customer BookDownload
// (a plain fetch on a paid-gated endpoint), this sends the admin Bearer token so
// the server's admin bypass lets it through even before the order is paid — used
// to proof a fully-generated book pre-payment. fetch→blob→click so a GCS 302
// saves the file instead of opening it inline. Throws so the caller can surface it.
async function downloadBookPdf(shareToken, title, barcode) {
  const res = await fetch(resolveAsset(`/api/stories/${shareToken}/book.pdf`), { headers: authHeader() });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { const d = await res.json(); if (d?.error) msg = d.error; } catch {}
    throw new Error(msg);
  }
  const bc = (barcode || '').trim() || shareToken;
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `story_${bc}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Exact-print preview geometry. One interior text page is a 200×210mm leaf
// (half of the 400×210 spread). Rendered at true print px (96 DPI) so pt font
// sizes + mm padding map 1:1 with the PDF, then scaled to fit the column —
// mirrors the cover-title preview technique (PANEL_W_PX below).
const PRINT_PX_PER_MM = 96 / 25.4;
const LEAF_W_PX = 200 * PRINT_PX_PER_MM; // ≈ 755.9
const LEAF_H_PX = 210 * PRINT_PX_PER_MM; // ≈ 793.7

function SceneCard({ storyId, scene, onUpdated, onAllUpdated, isManual, textBubble, textDir = 'rtl', onDelete, onMove, canMoveUp, canMoveDown }) {
  const t = useT();
  // `textDir` prop = the story's INHERITED base direction (from personalization).
  const storyDir = textDir === 'ltr' ? 'ltr' : 'rtl';
  const [text, setText] = useState(scene.text || '');
  const [imagePrompt, setImagePrompt] = useState(scene.imagePrompt || '');
  // Per-scene on-page text size in pt; '' = automatic (length-based) sizing.
  const [fontSize, setFontSize] = useState(scene.fontSize != null ? String(scene.fontSize) : '');
  // Per-scene text-layout overrides. '' = inherit/default; a dragged position is
  // {x,y} (leaf %) or null (centered). See lib/sceneText.js for how they resolve.
  const [dirOverride, setDirOverride] = useState(scene.textDir === 'ltr' || scene.textDir === 'rtl' ? scene.textDir : '');
  const [alignOverride, setAlignOverride] = useState(['right', 'left', 'center'].includes(scene.textAlign) ? scene.textAlign : '');
  const [pos, setPos] = useState(normScenePos(scene.textPosition));
  const [savingLayout, setSavingLayout] = useState(false);
  const [layoutSavedAt, setLayoutSavedAt] = useState(0);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [generatingPrompt, setGeneratingPrompt] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [refSubject, setRefSubject] = useState('');
  const [addingRef, setAddingRef] = useState(false);
  const [removingRefIdx, setRemovingRefIdx] = useState(-1);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(0);
  const fileRef = useRef(null);
  const refFileRef = useRef(null);

  // Exact-print text preview: scale the full-size 200×210mm leaf down to the
  // column width so its pt font + bubble proportions match the printed page.
  const previewRef = useRef(null);
  const [previewScale, setPreviewScale] = useState(240 / LEAF_W_PX);
  useEffect(() => {
    const el = previewRef.current;
    if (!el) return undefined;
    const update = () => {
      const w = el.getBoundingClientRect().width;
      if (w) setPreviewScale(w / LEAF_W_PX);
    };
    update();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const referenceImages = scene.referenceImages || [];
  const savedFontSize = scene.fontSize != null ? String(scene.fontSize) : '';
  const textDirty = text !== (scene.text || '');
  const promptDirty = imagePrompt !== (scene.imagePrompt || '');
  const fontSizeDirty = fontSize !== savedFontSize;
  const busy = saving || regenerating || generatingPrompt || uploading || addingRef || removingRefIdx >= 0 || savingLayout;

  // Effective preview layout: overrides win, else inherit the story defaults.
  const effDir = dirOverride || storyDir;
  const positioned = !!pos && Number.isFinite(pos.x) && Number.isFinite(pos.y);
  const savedDir = scene.textDir === 'ltr' || scene.textDir === 'rtl' ? scene.textDir : '';
  const savedAlign = ['right', 'left', 'center'].includes(scene.textAlign) ? scene.textAlign : '';
  const savedPos = normScenePos(scene.textPosition);
  const layoutDirty = dirOverride !== savedDir
    || alignOverride !== savedAlign
    || (!!savedPos !== positioned)
    || (positioned && savedPos && (pos.x !== savedPos.x || pos.y !== savedPos.y));

  // Persist the current text-layout overrides for THIS scene, or — with
  // applyToAll — fan the same direction/alignment/position out to every scene.
  const saveLayout = async (applyToAll = false) => {
    setError('');
    setSavingLayout(true);
    try {
      const body = {
        textDir: dirOverride || null,
        textAlign: alignOverride || null,
        textPosition: positioned ? { x: pos.x, y: pos.y } : null,
        ...(applyToAll ? { applyToAll: true } : {}),
      };
      const res = await api.patch(`/api/admin/stories/${storyId}/scenes/${scene.index}`, body);
      if (res && Array.isArray(res.scenes)) onAllUpdated(res.scenes);
      else onUpdated(scene.index, res);
      setLayoutSavedAt(Date.now());
    } catch (e) {
      setError(e.message || 'فشل حفظ التنسيق');
    } finally {
      setSavingLayout(false);
    }
  };

  // Drag the text block within the preview. The preview box (previewRef) shares
  // the 200×210 leaf aspect, so a position measured as a % of the box maps 1:1
  // onto the printed/viewer text page. translate(-50%,-50%) anchors the block's
  // centre, so we clamp a little away from the edges. A move past a small
  // threshold counts as a drag (so a plain click doesn't jump the text).
  const startTextDrag = (e) => {
    if (busy) return;
    e.preventDefault();
    const startX = e.clientX, startY = e.clientY;
    let moved = false;
    const move = (ev) => {
      if (!moved && (Math.abs(ev.clientX - startX) > 3 || Math.abs(ev.clientY - startY) > 3)) moved = true;
      if (!moved) return;
      const box = previewRef.current?.getBoundingClientRect();
      if (!box || !box.width || !box.height) return;
      const x = ((ev.clientX - box.left) / box.width) * 100;
      const y = ((ev.clientY - box.top) / box.height) * 100;
      setPos({
        x: Math.round(Math.min(96, Math.max(4, x))),
        y: Math.round(Math.min(96, Math.max(4, y))),
      });
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const saveText = async () => {
    setError('');
    setSaving(true);
    try {
      const updated = await api.patch(`/api/admin/stories/${storyId}/scenes/${scene.index}`, {
        text,
        imagePrompt,
        fontSize: fontSize === '' ? null : Number(fontSize),
      });
      onUpdated(scene.index, updated);
      setSavedAt(Date.now());
    } catch (e) {
      setError(e.message || 'فشل الحفظ');
    } finally {
      setSaving(false);
    }
  };

  const regenerate = async () => {
    setError('');
    setRegenerating(true);
    try {
      // Send the current prompt draft so prompt edits take effect on this render.
      const res = await api.post(
        `/api/admin/stories/${storyId}/scenes/${scene.index}/regenerate-image`,
        { imagePrompt },
      );
      onUpdated(scene.index, res.scene);
      setSavedAt(Date.now());
    } catch (e) {
      setError(e.message || 'فشل توليد الصورة');
    } finally {
      setRegenerating(false);
    }
  };

  // Generate an English image prompt from the current (possibly unsaved) scene
  // text, then drop it into the image-prompt box for review. The admin can edit
  // it and hit "regenerate image". Nothing is persisted until they save/regenerate.
  const generatePrompt = async () => {
    setError('');
    setGeneratingPrompt(true);
    try {
      const res = await api.post(
        `/api/admin/stories/${storyId}/scenes/${scene.index}/generate-prompt`,
        { text },
      );
      if (typeof res.imagePrompt === 'string') setImagePrompt(res.imagePrompt);
    } catch (e) {
      setError(e.message || 'فشل توليد الوصف');
    } finally {
      setGeneratingPrompt(false);
    }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const updated = await api.postForm(`/api/admin/stories/${storyId}/scenes/${scene.index}/upload`, fd);
      onUpdated(scene.index, updated);
      setSavedAt(Date.now());
    } catch (e) {
      setError(e.message || 'فشل رفع الصورة');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const addReferenceImage = async (file) => {
    if (!file) return;
    setError('');
    setAddingRef(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      if (refSubject.trim()) fd.append('subject', refSubject.trim());
      const updated = await api.postForm(
        `/api/admin/stories/${storyId}/scenes/${scene.index}/reference-images`,
        fd,
      );
      onUpdated(scene.index, updated);
      setRefSubject('');
    } catch (e) {
      setError(e.message || 'فشل إضافة الصورة المرجعية');
    } finally {
      setAddingRef(false);
      if (refFileRef.current) refFileRef.current.value = '';
    }
  };

  const removeReferenceImage = async (refIndex) => {
    setError('');
    setRemovingRefIdx(refIndex);
    try {
      const updated = await api.delete(
        `/api/admin/stories/${storyId}/scenes/${scene.index}/reference-images/${refIndex}`,
      );
      onUpdated(scene.index, updated);
    } catch (e) {
      setError(e.message || 'فشل حذف الصورة المرجعية');
    } finally {
      setRemovingRefIdx(-1);
    }
  };

  const imgSrc = bust(scene.imageUrl, savedAt);

  // Text-layout controls under the preview: direction (auto/RTL/LTR), paragraph
  // alignment (auto/right/center/left), and save / apply-to-all / re-center.
  const dirBtnClass = (v) => `admin-seg-btn${dirOverride === v ? ' is-active' : ''}`;
  const alignBtnClass = (v) => `admin-seg-btn${alignOverride === v ? ' is-active' : ''}`;
  const renderLayoutControls = () => (
    <div className="admin-scene-layout">
      <div className="admin-seg-field">
        <span className="admin-field-label">اتجاه النص</span>
        <div className="admin-seg" role="group" aria-label="اتجاه النص">
          <button type="button" className={dirBtnClass('')} onClick={() => setDirOverride('')} disabled={busy}>تلقائي</button>
          <button type="button" className={dirBtnClass('rtl')} onClick={() => setDirOverride('rtl')} disabled={busy}>عربي RTL</button>
          <button type="button" className={dirBtnClass('ltr')} onClick={() => setDirOverride('ltr')} disabled={busy}>English LTR</button>
        </div>
      </div>
      <div className="admin-seg-field">
        <span className="admin-field-label">محاذاة النص</span>
        <div className="admin-seg" role="group" aria-label="محاذاة النص">
          <button type="button" className={alignBtnClass('')} onClick={() => setAlignOverride('')} disabled={busy}>تلقائي</button>
          <button type="button" className={alignBtnClass('right')} onClick={() => setAlignOverride('right')} disabled={busy} title="محاذاة لليمين">يمين ⭤</button>
          <button type="button" className={alignBtnClass('center')} onClick={() => setAlignOverride('center')} disabled={busy} title="توسيط">وسط</button>
          <button type="button" className={alignBtnClass('left')} onClick={() => setAlignOverride('left')} disabled={busy} title="محاذاة لليسار">يسار ⭤</button>
        </div>
      </div>
      <div className="admin-scene-actions">
        <button type="button" className="submit-btn" onClick={() => saveLayout(false)} disabled={busy || !layoutDirty}>
          {savingLayout ? 'جارٍ الحفظ…' : 'حفظ التنسيق'}
        </button>
        <button type="button" className="ghost-btn" onClick={() => saveLayout(true)} disabled={busy} title="تطبيق الاتجاه والمحاذاة والموضع الحالي على كل مشاهد القصة">
          تطبيق على كل المشاهد
        </button>
        {positioned && (
          <button type="button" className="admin-link-btn" onClick={() => setPos(null)} disabled={busy}>توسيط النص</button>
        )}
        {!layoutDirty && layoutSavedAt > 0 && <span className="admin-scene-meta">تم الحفظ ✓</span>}
      </div>
    </div>
  );

  return (
    <div className="admin-scene-card">
      <div className="admin-scene-media">
        {imgSrc ? (
          <img src={imgSrc} alt={`مشهد ${scene.index + 1}`} className="admin-scene-img" />
        ) : (
          <div className="admin-scene-img admin-scene-img-empty">لا توجد صورة</div>
        )}
        <button
          type="button"
          className="submit-btn"
          onClick={regenerate}
          disabled={busy || !imagePrompt.trim()}
        >
          {regenerating ? 'جارٍ التوليد…' : 'إعادة توليد الصورة'}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => uploadImage(e.target.files?.[0])}
        />
        <button
          type="button"
          className="ghost-btn"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
        >
          {uploading ? 'جارٍ الرفع…' : 'رفع صورة من جهازك'}
        </button>
        {(scene.regenerationsUsed > 0 || scene.imageBackend) && (
          <span className="admin-scene-meta">
            {scene.regenerationsUsed > 0 && <>عدد مرات التوليد: {scene.regenerationsUsed} </>}
            <BackendBadge backend={scene.imageBackend} />
          </span>
        )}

        {/* Exact preview of how this scene's text renders on the printed page —
            true pt size (manual override or auto length-class), Cairo, on the
            cream leaf, with the hand-drawn bubble when enabled. Reflects the
            direction / alignment / dragged position overrides live, and the text
            block itself is DRAGGABLE to reposition it. */}
        <div className="admin-scene-text-preview-block">
          <span className="admin-field-label">معاينة النص كما سيظهر في الصفحة (اسحب النص لتغيير موضعه)</span>
          <div className="admin-scene-text-preview" ref={previewRef}>
            <div
              className="admin-print-leaf-stage"
              style={{ width: `${LEAF_W_PX}px`, height: `${LEAF_H_PX}px`, transform: `scale(${previewScale})` }}
            >
              <div className="admin-print-leaf">
                {(() => {
                  const hasPt = fontSize !== '' && Number(fontSize) > 0;
                  const body = text.trim() ? text : 'سيظهر نص المشهد هنا';
                  const blockPos = positioned ? { left: `${pos.x}%`, top: `${pos.y}%`, cursor: 'move' } : { cursor: 'move' };
                  const posClass = positioned ? ' scene-text-positioned' : '';
                  const noBubble = !(textBubble && text.trim());
                  const pStyle = {
                    ...(hasPt ? { fontSize: `${Number(fontSize)}pt`, lineHeight: 1.8 } : null),
                    opacity: text.trim() ? 1 : 0.45,
                    ...(alignOverride ? { textAlign: alignOverride } : null),
                  };
                  const p = (
                    <p
                      dir={effDir}
                      className={`${hasPt ? 'scene-text' : `scene-text ${sceneTextSizeClass(body)}`}${noBubble ? posClass : ''}`.trim()}
                      style={noBubble ? { ...pStyle, ...blockPos } : pStyle}
                      onPointerDown={noBubble ? startTextDrag : undefined}
                    >
                      {body}
                    </p>
                  );
                  return noBubble ? p : (
                    <span className={`bubble-wrap${posClass}`} style={blockPos} onPointerDown={startTextDrag}>
                      <TextBubble seed={scene.index} />
                      {p}
                    </span>
                  );
                })()}
              </div>
            </div>
          </div>
          {renderLayoutControls()}
        </div>
      </div>

      <div className="admin-scene-fields">
        <div className="admin-scene-prompt-header">
          <label className="admin-field-label">نص المشهد {scene.index + 1}</label>
          {isManual && (
            <div style={{ display: 'inline-flex', gap: 6 }}>
              <button type="button" className="ghost-btn" onClick={() => onMove(scene.index, -1)} disabled={busy || !canMoveUp} title="تحريك لأعلى">↑</button>
              <button type="button" className="ghost-btn" onClick={() => onMove(scene.index, 1)} disabled={busy || !canMoveDown} title="تحريك لأسفل">↓</button>
              <button type="button" className="admin-link-btn admin-link-danger" onClick={() => onDelete(scene.index)} disabled={busy}>حذف المشهد</button>
            </div>
          )}
        </div>
        <textarea
          className="admin-scene-textarea"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          dir={textDir}
        />

        <label className="admin-field-label">حجم الخط (نقطة pt — اتركه فارغًا للحجم التلقائي)</label>
        <input
          className="admin-title-input"
          type="number"
          min="6"
          max="48"
          step="0.5"
          dir="ltr"
          style={{ maxWidth: 160 }}
          value={fontSize}
          onChange={(e) => setFontSize(e.target.value)}
          placeholder="تلقائي"
        />

        <div className="admin-scene-prompt-header">
          <label className="admin-field-label">وصف الصورة (image prompt)</label>
          <button
            type="button"
            className="ghost-btn"
            onClick={generatePrompt}
            disabled={busy || !text.trim()}
            title="يولّد وصف الصورة بالإنجليزية من نص المشهد أعلاه. عدّله ثم اضغط «إعادة توليد الصورة»."
          >
            {generatingPrompt ? 'جارٍ التوليد…' : '✨ توليد الوصف من نص المشهد'}
          </button>
        </div>
        <textarea
          className="admin-scene-textarea"
          value={imagePrompt}
          onChange={(e) => setImagePrompt(e.target.value)}
          rows={4}
          dir="ltr"
        />

        <label className="admin-field-label">صور مرجعية لهذا المشهد</label>
        <p className="admin-scene-meta">
          تُرسَل هذه الصور مع توليد هذا المشهد فقط (إضافةً إلى مرجع الشخصية) — مثلاً غرض أو خلفية يجب أن تظهر.
        </p>
        {referenceImages.length > 0 && (
          <div className="admin-scene-refs">
            {referenceImages.map((ref, i) => (
              <div className="admin-scene-ref" key={`${ref.url}-${i}`}>
                <img
                  src={bust(ref.url)}
                  alt={ref.subject || `مرجع ${i + 1}`}
                  className="admin-scene-ref-img"
                />
                <button
                  type="button"
                  className="admin-scene-ref-remove"
                  onClick={() => removeReferenceImage(i)}
                  disabled={busy}
                  title="حذف"
                  aria-label="حذف الصورة المرجعية"
                >
                  {removingRefIdx === i ? '…' : '✕'}
                </button>
                {ref.subject && <span className="admin-scene-ref-subject">{ref.subject}</span>}
              </div>
            ))}
          </div>
        )}
        <div className="admin-scene-ref-add">
          <input
            className="admin-scene-ref-subject-input"
            type="text"
            value={refSubject}
            onChange={(e) => setRefSubject(e.target.value)}
            placeholder="ماذا تمثل الصورة؟ (اختياري) مثال: دراجة حمراء"
            dir="rtl"
            disabled={busy}
          />
          <input
            ref={refFileRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => addReferenceImage(e.target.files?.[0])}
          />
          <button
            type="button"
            className="ghost-btn"
            onClick={() => refFileRef.current?.click()}
            disabled={busy}
          >
            {addingRef ? 'جارٍ الإضافة…' : '+ إضافة صورة مرجعية'}
          </button>
        </div>

        {error && <p className="auth-error">{error}</p>}

        <div className="admin-scene-actions">
          <button
            type="button"
            className="submit-btn"
            onClick={saveText}
            disabled={busy || (!textDirty && !promptDirty && !fontSizeDirty)}
          >
            {saving ? 'جارٍ الحفظ…' : 'حفظ النص'}
          </button>
          {!textDirty && !promptDirty && !fontSizeDirty && savedAt > 0 && (
            <span className="admin-scene-meta">تم الحفظ ✓</span>
          )}
        </div>
      </div>
    </div>
  );
}

// The cover title's default resting place (panel-relative %: x = horizontal
// centre, y = top edge) when the admin hasn't dragged it yet. Mirrors the CSS
// default (.cover-title { top: 5%; left: 50% }) closely enough to start from.
const DEFAULT_TITLE_POS = { x: 50, y: 6 };
function normTitlePos(p) {
  return p && Number.isFinite(p.x) && Number.isFinite(p.y)
    ? { x: p.x, y: p.y }
    : { ...DEFAULT_TITLE_POS };
}

// Normalize a saved coverTitleStyle (possibly a Mongoose subdoc) into a plain
// editable draft. Keeps only the whitelisted per-line props so the editor and
// the server agree on shape.
function normTitleStyle(s) {
  const mw = s && Number.isFinite(s.maxWidthPct) ? s.maxWidthPct : null;
  const gap = s && Number.isFinite(s.lineGap) ? s.lineGap : null;
  const linesIn = Array.isArray(s?.lines) ? s.lines : [];
  const lines = linesIn.map((l) => ({
    fill: l?.fill ?? null,
    outline: l?.outline ?? null,
    outlineWidth: Number.isFinite(l?.outlineWidth) ? l.outlineWidth : null,
    font: l?.font ?? null,
    fontSize: Number.isFinite(l?.fontSize) ? l.fontSize : null,
    letterSpacing: Number.isFinite(l?.letterSpacing) ? l.letterSpacing : null,
    weight: Number.isFinite(l?.weight) ? l.weight : null,
  }));
  return { maxWidthPct: mw, lineGap: gap, lines };
}

// The print front panel is 200mm wide; at 96dpi that's this many CSS px. The
// admin preview scales the title by (boxWidth / PANEL_W_PX) so its pt sizes
// render at the same proportion they print at.
const PANEL_W_PX = 200 * (96 / 25.4); // ≈ 755.9

// Print geometry mirrored from server coverSpread.js (mm). The stored
// cover.png is ALREADY the trimmed 200×210mm panel — the printer's cut falls
// exactly on its edges — so the editor previews the EXPANDED front region
// (panel + 2cm bleed on every side, 240×250mm) cropped out of the full
// wraparound spread, and draws the cut line 2cm inside that expanded frame.
// Everything outside the dashed line is what the trim removes.
const COVER_PANEL_W_MM = 200;
const COVER_PANEL_H_MM = 210;
const COVER_SPINE_MM = 28;
const COVER_BLEED_MM = 20;
// Spread layout: |bleed|front|spine|back|bleed| (RTL book: front = LEFT half).
// The front-with-bleed region is the leftmost EXPANDED_W of the spread; its
// right-side 2cm comes out of the spine dead-zone, which wraps away like bleed.
const COVER_SPREAD_W_MM = 2 * COVER_PANEL_W_MM + COVER_SPINE_MM + 2 * COVER_BLEED_MM; // 468
const COVER_EXPANDED_W_MM = COVER_PANEL_W_MM + 2 * COVER_BLEED_MM; // 240
const COVER_EXPANDED_H_MM = COVER_PANEL_H_MM + 2 * COVER_BLEED_MM; // 250

// Empty numeric input → null (auto); otherwise the parsed number.
function numOrNull(v) {
  if (v === '' || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
const TITLE_CTL_STYLE = { display: 'flex', flexDirection: 'column', gap: 2, fontSize: '0.78rem', color: '#6b5a42' };

function CoverCard({ story, onCoverUpdated }) {
  const [imagePrompt, setImagePrompt] = useState(story.coverImagePrompt || '');
  const [title, setTitle] = useState(story.title || '');
  const [regenerating, setRegenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [savingTheme, setSavingTheme] = useState(false);
  const [savingBubble, setSavingBubble] = useState(false);
  const [savingTitle, setSavingTitle] = useState(false);
  const [titleSaved, setTitleSaved] = useState(false);
  const [titlePos, setTitlePos] = useState(normTitlePos(story.coverTitlePosition));
  const [savingPos, setSavingPos] = useState(false);
  // Admin-only overlay marking the print clear-cut (2cm trim) line on the front
  // panel, so the title can be positioned safely inside it. Never shown to
  // customers — it lives only in this editor preview.
  const [showTrimGuide, setShowTrimGuide] = useState(true);
  // Cover-title designer draft: { maxWidthPct, lines:[{...}] }. Seeded from the
  // saved style; edits update the live preview and persist on save.
  const [titleStyle, setTitleStyle] = useState(() => normTitleStyle(story.coverTitleStyle));
  const [savingStyle, setSavingStyle] = useState(false);
  const [styleSaved, setStyleSaved] = useState(false);
  // In-context title editor: which line is selected, whether the popover is
  // open, and its draggable position (px, relative to the preview box; null =
  // the CSS default anchor).
  const [selectedLine, setSelectedLine] = useState(0);
  const [stylePopover, setStylePopover] = useState(false);
  const [popoverPos, setPopoverPos] = useState(null);
  // Scale that turns the small preview box into a true miniature of the 200mm
  // print panel, so per-line pt sizes look exactly as they print.
  const [previewScale, setPreviewScale] = useState(360 / PANEL_W_PX);
  const [refSubject, setRefSubject] = useState('');
  const [addingRef, setAddingRef] = useState(false);
  const [removingRefIdx, setRemovingRefIdx] = useState(-1);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(0);
  const fileRef = useRef(null);
  const refFileRef = useRef(null);
  const previewRef = useRef(null);
  // The inner trim rectangle (the real 200×210 panel) inside the expanded
  // preview — the frame the title position/scale are measured against.
  const panelRef = useRef(null);

  const referenceImages = story.coverReferenceImages || [];
  const busy = regenerating || uploading || addingRef || removingRefIdx >= 0;
  const titleDirty = title !== (story.title || '');
  const savedPos = normTitlePos(story.coverTitlePosition);
  const posDirty = titlePos.x !== savedPos.x || titlePos.y !== savedPos.y;

  // Drag the title within the inner trim rectangle (panelRef): a position
  // measured as a % of that rect maps 1:1 onto the printed/viewer 200×210
  // panel, even though the preview box around it also shows the 2cm bleed.
  // translateX(-50%) (in CoverTitle) anchors x as the title's centre, so we
  // clamp x away from the edges and y to the top region — the title can never
  // be parked in the bleed, where the cut would eat it.
  const startTitleDrag = (e) => {
    if (busy) return;
    e.preventDefault();
    const startX = e.clientX, startY = e.clientY;
    // Which line was pressed (the span carries data-line) — used if it's a click.
    const lineIdx = Number(e.target?.dataset?.line);
    let moved = false;
    const move = (ev) => {
      if (!moved && (Math.abs(ev.clientX - startX) > 4 || Math.abs(ev.clientY - startY) > 4)) moved = true;
      if (!moved) return;
      const box = panelRef.current?.getBoundingClientRect();
      if (!box || !box.width || !box.height) return;
      const x = ((ev.clientX - box.left) / box.width) * 100;
      const y = ((ev.clientY - box.top) / box.height) * 100;
      setTitlePos({
        x: Math.round(Math.min(95, Math.max(5, x))),
        y: Math.round(Math.min(92, Math.max(0, y))),
      });
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      // A click (no drag) opens the in-context editor for the pressed line.
      if (!moved) openStylePopover(lineIdx);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const saveTitlePos = async () => {
    setError('');
    setSavingPos(true);
    try {
      const updated = await api.patch(`/api/admin/stories/${story._id}`, { coverTitlePosition: titlePos });
      onCoverUpdated({ coverTitlePosition: updated.coverTitlePosition });
      setTitlePos(normTitlePos(updated.coverTitlePosition));
    } catch (e) {
      setError(e.message || 'فشل حفظ موقع العنوان');
    } finally {
      setSavingPos(false);
    }
  };

  const resetTitlePos = () => setTitlePos({ ...DEFAULT_TITLE_POS });

  // ── Cover-title designer ──────────────────────────────────────────────────
  // Lines are derived from the title with the SAME splitter the renderer uses,
  // so each editor row maps to a rendered line.
  const titleLines = splitTitleLines(title);
  const lineStyleAt = (i) => (titleStyle.lines && titleStyle.lines[i]) || {};
  // Merge a patch into line i, growing the lines array as needed.
  const patchLine = (i, patch) => {
    setTitleStyle((s) => {
      const lines = [...(s.lines || [])];
      while (lines.length <= i) lines.push({});
      lines[i] = { ...lines[i], ...patch };
      return { ...s, lines };
    });
    setStyleSaved(false);
  };
  const setMaxWidth = (v) => {
    setTitleStyle((s) => ({ ...s, maxWidthPct: v }));
    setStyleSaved(false);
  };
  const setLineGap = (v) => {
    setTitleStyle((s) => ({ ...s, lineGap: v }));
    setStyleSaved(false);
  };

  // Open the editor popover for a given line, anchored near the top of the
  // preview (so it doesn't cover the title) — then it's freely draggable.
  const openStylePopover = (i) => {
    setSelectedLine(Number.isFinite(i) ? i : 0);
    const box = previewRef.current;
    setPopoverPos(box ? { x: 8, y: Math.round(box.offsetHeight * 0.24) } : { x: 8, y: 90 });
    setStylePopover(true);
  };

  // Drag the popover by its header. Positions are stored relative to the preview
  // box; the box gets overflow:visible while editing so it can move freely.
  const startPopoverDrag = (e) => {
    if (e.target.closest('.title-style-close')) return;
    e.preventDefault();
    const startX = e.clientX, startY = e.clientY;
    const base = popoverPos || { x: 8, y: 90 };
    const move = (ev) => setPopoverPos({ x: base.x + (ev.clientX - startX), y: base.y + (ev.clientY - startY) });
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const saveTitleStyle = async () => {
    setError('');
    setSavingStyle(true);
    try {
      // Persist only as many lines as the current title actually has.
      const payload = {
        maxWidthPct: Number.isFinite(titleStyle.maxWidthPct) ? titleStyle.maxWidthPct : null,
        lineGap: Number.isFinite(titleStyle.lineGap) ? titleStyle.lineGap : null,
        lines: (titleStyle.lines || []).slice(0, titleLines.length),
      };
      const updated = await api.patch(`/api/admin/stories/${story._id}`, { coverTitleStyle: payload });
      onCoverUpdated({ coverTitleStyle: updated.coverTitleStyle });
      setTitleStyle(normTitleStyle(updated.coverTitleStyle));
      setStyleSaved(true);
    } catch (e) {
      setError(e.message || 'فشل حفظ تنسيق العنوان');
    } finally {
      setSavingStyle(false);
    }
  };

  const resetTitleStyle = async () => {
    setError('');
    setSavingStyle(true);
    try {
      const updated = await api.patch(`/api/admin/stories/${story._id}`, { coverTitleStyle: null });
      onCoverUpdated({ coverTitleStyle: updated.coverTitleStyle });
      setTitleStyle(normTitleStyle(updated.coverTitleStyle));
      setStyleSaved(false);
    } catch (e) {
      setError(e.message || 'فشل إعادة ضبط التنسيق');
    } finally {
      setSavingStyle(false);
    }
  };

  // The control grid for ONE title line — shown inside the popover for the
  // currently selected line.
  const lineControls = (i) => {
    const l = lineStyleAt(i);
    return (
      <div className="title-style-grid">
        <label style={TITLE_CTL_STYLE}>الخط
          <select value={l.font || ''} onChange={(e) => patchLine(i, { font: e.target.value || null })}>
            <option value="">افتراضي</option>
            {COVER_FONTS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
          </select>
        </label>
        <label style={TITLE_CTL_STYLE}>الحجم (pt)
          <input type="number" min="8" max="120" step="1"
            value={l.fontSize ?? ''} placeholder="تلقائي"
            onChange={(e) => patchLine(i, { fontSize: numOrNull(e.target.value) })} />
        </label>
        <label style={TITLE_CTL_STYLE}>السماكة
          <select value={l.weight ?? ''} onChange={(e) => patchLine(i, { weight: e.target.value ? Number(e.target.value) : null })}>
            <option value="">تلقائي</option>
            {[400, 500, 600, 700, 800, 900].map((w) => <option key={w} value={w}>{w}</option>)}
          </select>
        </label>
        <label style={TITLE_CTL_STYLE}>اللون
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, height: 28 }}>
            <input type="checkbox" checked={l.fill != null} onChange={(e) => patchLine(i, { fill: e.target.checked ? (l.fill || '#ffd166') : null })} />
            {l.fill != null && <input type="color" value={l.fill} onChange={(e) => patchLine(i, { fill: e.target.value })} />}
          </span>
        </label>
        <label style={TITLE_CTL_STYLE}>الحدود
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, height: 28 }}>
            <input type="checkbox" checked={l.outline != null} onChange={(e) => patchLine(i, { outline: e.target.checked ? (l.outline || '#ffffff') : null })} />
            {l.outline != null && <input type="color" value={l.outline} onChange={(e) => patchLine(i, { outline: e.target.value })} />}
          </span>
        </label>
        <label style={TITLE_CTL_STYLE}>سماكة الحدود
          <input type="number" min="0" max="0.4" step="0.01"
            value={l.outlineWidth ?? ''} placeholder="تلقائي"
            onChange={(e) => patchLine(i, { outlineWidth: numOrNull(e.target.value) })} />
        </label>
        <label style={TITLE_CTL_STYLE}>تباعد الأحرف (em)
          <input type="number" min="-0.2" max="0.6" step="0.01"
            value={l.letterSpacing ?? ''} placeholder="0"
            onChange={(e) => patchLine(i, { letterSpacing: numOrNull(e.target.value) })} />
        </label>
      </div>
    );
  };

  const saveTitle = async () => {
    setError('');
    setSavingTitle(true);
    try {
      const updated = await api.patch(`/api/admin/stories/${story._id}`, { title });
      onCoverUpdated({ title: updated.title });
      setTitle(updated.title || '');
      setTitleSaved(true);
    } catch (e) {
      setError(e.message || 'فشل حفظ العنوان');
    } finally {
      setSavingTitle(false);
    }
  };

  const regenerate = async () => {
    setError('');
    setRegenerating(true);
    try {
      const res = await api.post(`/api/admin/stories/${story._id}/cover/regenerate-image`, { imagePrompt });
      onCoverUpdated({
        coverImageUrl: res.coverImageUrl,
        coverBackImageUrl: res.coverBackImageUrl,
        coverSpreadImageUrl: res.coverSpreadImageUrl,
        coverImagePrompt: res.coverImagePrompt,
        coverTitleTheme: res.coverTitleTheme,
      });
      if (typeof res.coverImagePrompt === 'string') setImagePrompt(res.coverImagePrompt);
      setSavedAt(Date.now());
    } catch (e) {
      setError(e.message || 'فشل توليد الغلاف');
    } finally {
      setRegenerating(false);
    }
  };

  const uploadCover = async (file) => {
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await api.postForm(`/api/admin/stories/${story._id}/cover/upload`, fd);
      // The server clears the old generated spread/back on upload so the book
      // uses the uploaded front panel — mirror that here so the preview updates.
      onCoverUpdated({
        coverImageUrl: res.coverImageUrl,
        coverSpreadImageUrl: res.coverSpreadImageUrl ?? null,
        coverBackImageUrl: res.coverBackImageUrl ?? null,
        coverTitleTheme: res.coverTitleTheme,
      });
      setSavedAt(Date.now());
    } catch (e) {
      setError(e.message || 'فشل رفع الغلاف');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const addReferenceImage = async (file) => {
    if (!file) return;
    setError('');
    setAddingRef(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      if (refSubject.trim()) fd.append('subject', refSubject.trim());
      const res = await api.postForm(`/api/admin/stories/${story._id}/cover/reference-images`, fd);
      onCoverUpdated({ coverReferenceImages: res.coverReferenceImages });
      setRefSubject('');
    } catch (e) {
      setError(e.message || 'فشل إضافة الصورة المرجعية');
    } finally {
      setAddingRef(false);
      if (refFileRef.current) refFileRef.current.value = '';
    }
  };

  const removeReferenceImage = async (refIndex) => {
    setError('');
    setRemovingRefIdx(refIndex);
    try {
      const res = await api.delete(`/api/admin/stories/${story._id}/cover/reference-images/${refIndex}`);
      onCoverUpdated({ coverReferenceImages: res.coverReferenceImages });
    } catch (e) {
      setError(e.message || 'فشل حذف الصورة المرجعية');
    } finally {
      setRemovingRefIdx(-1);
    }
  };

  const setTitleTheme = async (coverTitleTheme) => {
    if (savingTheme || busy || story.coverTitleTheme === coverTitleTheme) return;
    setSavingTheme(true);
    try {
      const updated = await api.patch(`/api/admin/stories/${story._id}`, { coverTitleTheme });
      onCoverUpdated({ coverTitleTheme: updated.coverTitleTheme });
    } catch (e) {
      setError(e.message || 'فشل حفظ لون العنوان');
    } finally {
      setSavingTheme(false);
    }
  };

  // Toggle the hand-made off-white bubble drawn behind every scene's text.
  // Saving bumps the story's updatedAt, which invalidates the cached book PDF.
  const toggleTextBubble = async (on) => {
    if (savingBubble || busy) return;
    setSavingBubble(true);
    try {
      const updated = await api.patch(`/api/admin/stories/${story._id}`, { textBubble: on });
      onCoverUpdated({ textBubble: updated.textBubble });
    } catch (e) {
      setError(e.message || 'فشل حفظ خيار الفقاعة');
    } finally {
      setSavingBubble(false);
    }
  };

  const imgSrc = bust(story.coverImageUrl, savedAt);
  // Expanded preview: crop the front-with-bleed region out of the stored full
  // spread. Legacy stories (and split-failure covers, where the "spread" is the
  // same raw image as the front) have no separate spread — fall back to the
  // trimmed panel with the cut line sitting on its edges (inset 0).
  const spreadUrl = story.coverSpreadImageUrl;
  const hasBleed = !!spreadUrl && spreadUrl !== story.coverImageUrl;
  const spreadSrc = hasBleed ? bust(spreadUrl, savedAt) : null;
  const trimInsetX = hasBleed ? (COVER_BLEED_MM / COVER_EXPANDED_W_MM) * 100 : 0;
  const trimInsetY = hasBleed ? (COVER_BLEED_MM / COVER_EXPANDED_H_MM) * 100 : 0;

  // Keep previewScale in sync with the actual rendered width of the inner trim
  // rectangle (the true panel frame, excluding the bleed border) so the title
  // miniature stays true-to-print as the layout/viewport changes.
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return undefined;
    const update = () => {
      const w = el.getBoundingClientRect().width;
      if (w) setPreviewScale(w / PANEL_W_PX);
    };
    update();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [imgSrc]);

  return (
    <div className="admin-scene-card">
      <div className="admin-scene-media">
        {imgSrc ? (
          <div
            className={`admin-cover-preview admin-cover-preview--panel${stylePopover ? ' is-editing' : ''}`}
            ref={previewRef}
            style={hasBleed ? { aspectRatio: `${COVER_EXPANDED_W_MM} / ${COVER_EXPANDED_H_MM}` } : undefined}
          >
            {/* Art in its own clipping frame: while the style popover is open
                the preview box goes overflow:visible so the popover can escape,
                and the ~2×-wide spread crop must not spill out with it. */}
            <div className="admin-cover-art">
              {hasBleed ? (
                /* Front-with-bleed region cropped out of the stored spread
                   (|bleed|front|spine|back|bleed|; RTL book → front = LEFT
                   half). The spread's height equals the expanded frame's, so
                   pinning the image's left edge shows exactly this region and
                   the frame clips away the spine/back. left/right are forced
                   inline so the RTL page direction can't resolve the
                   over-constrained position to right:0 and flip the crop to
                   the back cover. */
                <img
                  src={spreadSrc}
                  alt="الغلاف"
                  className="admin-scene-img"
                  style={{
                    left: 0,
                    right: 'auto',
                    width: `${(COVER_SPREAD_W_MM / COVER_EXPANDED_W_MM) * 100}%`,
                    objectFit: 'fill',
                  }}
                />
              ) : (
                <img src={imgSrc} alt="الغلاف" className="admin-scene-img" />
              )}
            </div>
            {showTrimGuide && (
              <div
                className="admin-cover-trim-guide"
                aria-hidden="true"
                style={{
                  top: `${trimInsetY}%`,
                  bottom: `${trimInsetY}%`,
                  left: `${trimInsetX}%`,
                  right: `${trimInsetX}%`,
                }}
              >
                <span className="admin-cover-trim-label">خط القص — 2 سم</span>
              </div>
            )}
            {/* The panel rect is the inner trim rectangle — the same frame the
                title % position is stored in — so title coordinates stay 1:1
                with the printed panel even though the box around it shows the
                bleed. Inside it, the full-size panel "stage" is scaled down:
                the title lays out in the SAME pixel space and containing block
                as the print panel, so its shrink-to-fit width and line wrapping
                are identical to the PDF. (Scaling the title alone left its
                container box-sized, which squeezed a 2-word painted line into
                one word per line.) transform-origin top-left pins the stage to
                the rect corner; previewScale = panelRect.width / PANEL_W_PX.
                pointer-events:none lets the image/trim-guide beneath stay
                clickable; the title re-enables them via
                .admin-cover-title-stage .cover-title in App.css. */}
            <div
              className="admin-cover-panel-rect"
              ref={panelRef}
              style={{
                top: `${trimInsetY}%`,
                bottom: `${trimInsetY}%`,
                left: `${trimInsetX}%`,
                right: `${trimInsetX}%`,
              }}
            >
              <div
                className="admin-cover-title-stage"
                style={{
                  width: `${PANEL_W_PX}px`,
                  height: `${PANEL_W_PX * (COVER_PANEL_H_MM / COVER_PANEL_W_MM)}px`,
                  transform: `scale(${previewScale})`,
                }}
              >
                <CoverTitle
                  title={title}
                  titleColors={story.coverTitleColors}
                  titleTheme={story.coverTitleTheme === 'light' ? 'light' : 'dark'}
                  titleStyle={titleStyle}
                  position={titlePos}
                  onLineClick={(i) => openStylePopover(i)}
                  selectedLine={stylePopover ? selectedLine : -1}
                  rootProps={{
                    className: busy ? 'admin-cover-title-handle' : 'admin-cover-title-handle is-draggable',
                    onPointerDown: startTitleDrag,
                    title: 'اسحب لتحريك العنوان — انقر سطراً لتنسيقه',
                  }}
                />
              </div>
            </div>
            {stylePopover && titleLines.length > 0 && (
              <div
                className="title-style-popover" dir="rtl"
                style={popoverPos ? { left: popoverPos.x, top: popoverPos.y, transform: 'none' } : undefined}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <div className="title-style-popover-head title-style-drag" onPointerDown={startPopoverDrag}>
                  <span>⠿ تصميم العنوان</span>
                  <button type="button" className="title-style-close" onClick={() => setStylePopover(false)} aria-label="إغلاق">✕</button>
                </div>
                {titleLines.length > 1 && (
                  <div className="title-style-chips">
                    {titleLines.map((ln, i) => (
                      <button
                        key={i}
                        type="button"
                        className={`title-style-chip${selectedLine === i ? ' is-active' : ''}`}
                        onClick={() => setSelectedLine(i)}
                        title={ln}
                      >
                        سطر {i + 1}
                      </button>
                    ))}
                  </div>
                )}
                <div className="title-style-line-text">«{titleLines[selectedLine] || ''}»</div>
                {lineControls(selectedLine)}
                <div className="title-style-globals">
                  <label className="title-style-global">
                    <span>عرض الصندوق: {titleStyle.maxWidthPct != null ? `${titleStyle.maxWidthPct}%` : 'تلقائي'}</span>
                    <input type="range" min="40" max="100" step="1"
                      value={titleStyle.maxWidthPct != null ? titleStyle.maxWidthPct : 100}
                      onChange={(e) => setMaxWidth(Number(e.target.value))} />
                  </label>
                  <label className="title-style-global">
                    <span>تباعد الأسطر: {titleStyle.lineGap != null ? `${titleStyle.lineGap}em` : 'تلقائي'}</span>
                    <input type="range" min="0" max="0.8" step="0.02"
                      value={titleStyle.lineGap != null ? titleStyle.lineGap : 0.22}
                      onChange={(e) => setLineGap(Number(e.target.value))} />
                  </label>
                </div>
                <div className="title-style-actions">
                  <button type="button" className="submit-btn" onClick={saveTitleStyle} disabled={savingStyle || busy}>
                    {savingStyle ? 'جارٍ الحفظ…' : 'حفظ'}
                  </button>
                  <button type="button" className="ghost-btn" onClick={resetTitleStyle} disabled={savingStyle || busy}>
                    إعادة ضبط
                  </button>
                  {styleSaved && <span className="admin-scene-meta">تم الحفظ ✓</span>}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="admin-scene-img admin-scene-img-empty">لا يوجد غلاف</div>
        )}
        <span className="admin-scene-meta">
          الغلاف الأمامي — اسحب العنوان لتحريكه، وانقر سطراً لتنسيقه <BackendBadge backend={story.coverImageBackend} />
        </span>
        {imgSrc && (
          <label className="admin-create-toggle admin-cover-guide-toggle">
            <input
              type="checkbox"
              checked={showTrimGuide}
              onChange={(e) => setShowTrimGuide(e.target.checked)}
            />
            <span>إظهار خط القص (2 سم) — للمعاينة فقط، لا يظهر في الكتاب</span>
          </label>
        )}
        {imgSrc && (
          <div className="admin-scene-actions">
            <button
              type="button"
              className="submit-btn"
              onClick={saveTitlePos}
              disabled={busy || savingPos || !posDirty}
            >
              {savingPos ? 'جارٍ الحفظ…' : 'حفظ موقع العنوان'}
            </button>
            <button
              type="button"
              className="ghost-btn"
              onClick={resetTitlePos}
              disabled={busy || savingPos || (titlePos.x === DEFAULT_TITLE_POS.x && titlePos.y === DEFAULT_TITLE_POS.y)}
            >
              إعادة الموقع الافتراضي
            </button>
          </div>
        )}
        {story.coverSpreadImageUrl && (
          <>
            <img
              src={bust(story.coverSpreadImageUrl, savedAt)}
              alt="الغلاف الكامل"
              className="admin-cover-spread-img"
            />
            <span className="admin-scene-meta">
              الغلاف الكامل للطباعة (أمامي يسار + خلفي يمين)
            </span>
          </>
        )}
        <button
          type="button"
          className="submit-btn"
          onClick={regenerate}
          disabled={busy}
        >
          {regenerating ? 'جارٍ التوليد…' : 'إعادة توليد الغلاف'}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => uploadCover(e.target.files?.[0])}
        />
        <button
          type="button"
          className="ghost-btn"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
        >
          {uploading ? 'جارٍ الرفع…' : 'رفع غلاف من جهازك'}
        </button>
      </div>

      <div className="admin-scene-fields">
        <label className="admin-field-label">عنوان القصة</label>
        <input
          className="admin-title-input"
          type="text"
          value={title}
          onChange={(e) => { setTitle(e.target.value); setTitleSaved(false); }}
          dir="rtl"
          placeholder="عنوان القصة كما يظهر على الغلاف والكتاب"
          disabled={busy || savingTitle}
        />
        <div className="admin-scene-actions">
          <button
            type="button"
            className="submit-btn"
            onClick={saveTitle}
            disabled={busy || savingTitle || !titleDirty}
          >
            {savingTitle ? 'جارٍ الحفظ…' : 'حفظ العنوان'}
          </button>
          {titleSaved && !titleDirty && (
            <span className="admin-scene-meta">تم الحفظ ✓</span>
          )}
        </div>

        <label className="admin-field-label">فكرة الغلاف (cover concept)</label>
        <textarea
          className="admin-scene-textarea"
          value={imagePrompt}
          onChange={(e) => setImagePrompt(e.target.value)}
          rows={6}
          dir="ltr"
          placeholder="صِف مشهد/فكرة الغلاف بالإنجليزية (المكان، العنصر البصري الأساسي، الجو). يُطبَّق نمط الرسم التلقائي (شبه واقعي مرسوم) دائماً — لا تكتب نوع الرسم. اتركه فارغاً للغلاف الافتراضي."
        />

        <label className="admin-field-label">صور مرجعية للغلاف</label>
        <p className="admin-scene-meta">
          تُرسَل هذه الصور مع توليد الغلاف (إضافةً إلى مرجع الشخصية) — مثلاً غرض أو خلفية يجب أن تظهر على الغلاف. تظهر بجانب البطل في الجهة اليسرى.
        </p>
        {referenceImages.length > 0 && (
          <div className="admin-scene-refs">
            {referenceImages.map((ref, i) => (
              <div className="admin-scene-ref" key={`${ref.url}-${i}`}>
                <img
                  src={bust(ref.url)}
                  alt={ref.subject || `مرجع ${i + 1}`}
                  className="admin-scene-ref-img"
                />
                <button
                  type="button"
                  className="admin-scene-ref-remove"
                  onClick={() => removeReferenceImage(i)}
                  disabled={busy}
                  title="حذف"
                  aria-label="حذف الصورة المرجعية"
                >
                  {removingRefIdx === i ? '…' : '✕'}
                </button>
                {ref.subject && <span className="admin-scene-ref-subject">{ref.subject}</span>}
              </div>
            ))}
          </div>
        )}
        <div className="admin-scene-ref-add">
          <input
            className="admin-scene-ref-subject-input"
            type="text"
            value={refSubject}
            onChange={(e) => setRefSubject(e.target.value)}
            placeholder="ماذا تمثل الصورة؟ (اختياري) مثال: دراجة حمراء"
            dir="rtl"
            disabled={busy}
          />
          <input
            ref={refFileRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => addReferenceImage(e.target.files?.[0])}
          />
          <button
            type="button"
            className="ghost-btn"
            onClick={() => refFileRef.current?.click()}
            disabled={busy}
          >
            {addingRef ? 'جارٍ الإضافة…' : '+ إضافة صورة مرجعية'}
          </button>
        </div>

        <div className="admin-cover-theme">
          <span className="admin-field-label">لون عنوان الغلاف</span>
          <div className="admin-cover-theme-actions">
            <button
              type="button"
              className={story.coverTitleTheme !== 'light' ? 'submit-btn' : 'ghost-btn'}
              onClick={() => setTitleTheme('dark')}
              disabled={savingTheme || busy}
            >
              نص داكن
            </button>
            <button
              type="button"
              className={story.coverTitleTheme === 'light' ? 'submit-btn' : 'ghost-btn'}
              onClick={() => setTitleTheme('light')}
              disabled={savingTheme || busy}
            >
              نص فاتح
            </button>
          </div>
        </div>

        {/* ── Cover-title designer: opens as a popover anchored on the title in
            the preview above. The button is a fallback entry point. ── */}
        <div className="admin-cover-theme">
          <span className="admin-field-label">تصميم العنوان</span>
          <p className="admin-scene-meta">انقر أي سطر من العنوان في المعاينة بالأعلى لتنسيقه (الخط، الألوان، الحدود، الحجم، التباعد).</p>
          <div className="admin-cover-theme-actions">
            <button
              type="button"
              className="ghost-btn"
              onClick={() => openStylePopover(0)}
              disabled={busy || !title.trim()}
            >
              ✎ فتح محرّر العنوان
            </button>
          </div>
        </div>

        {/* Hand-made off-white bubble behind every scene's text (a different
            organic shape per page). Applies to both the on-screen book and the
            printed PDFs. */}
        <div className="admin-cover-theme">
          <label className="admin-create-toggle">
            <input
              type="checkbox"
              checked={!!story.textBubble}
              onChange={(e) => toggleTextBubble(e.target.checked)}
              disabled={savingBubble || busy}
            />
            <span>فقاعة مرسومة يدوياً خلف النص{savingBubble ? ' — جارٍ الحفظ…' : ''}</span>
          </label>
        </div>

        {error && <p className="auth-error">{error}</p>}
      </div>
    </div>
  );
}

// Dedication-page portrait framing: pan (drag) + zoom the child's storybook
// portrait inside the fixed circular medallion so a badly-cropped face sits
// right. Saves dedicationImageTransform {posX,posY,zoom}; the BookViewer and the
// PDF apply the identical values, so this preview matches what prints.
function DedicationPortraitCard({ story, onUpdated }) {
  const portraitUrl = story.characterRefUrl || story.personalization?.characterImageUrl || null;
  const initial = story.dedicationImageTransform || {};
  const init = (v, d) => (Number.isFinite(v) ? v : d);
  const [posX, setPosX] = useState(init(initial.posX, 50));
  const [posY, setPosY] = useState(init(initial.posY, 50));
  const [zoom, setZoom] = useState(init(initial.zoom, 1));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const frameRef = useRef(null);
  const drag = useRef(null);

  // Dedication-page text: the title line (`dedication`, shared with the back
  // cover) and the keepsake paragraph (`dedicationText`). Empty falls back to
  // the auto-derived gendered defaults — the same ones bookPdf/BookViewer use.
  const personalization = story.personalization || {};
  const autoDedication = buildDedication(personalization);
  const autoText = buildDedicationText(personalization);
  const [dedication, setDedication] = useState(story.dedication || '');
  const [dedicationText, setDedicationText] = useState(story.dedicationText || '');
  const [savingText, setSavingText] = useState(false);
  const [savedText, setSavedText] = useState(false);
  const [textError, setTextError] = useState('');
  const textDirty = dedication !== (story.dedication || '') || dedicationText !== (story.dedicationText || '');

  const saveText = async () => {
    setTextError(''); setSavingText(true);
    try {
      const updated = await api.patch(`/api/admin/stories/${story._id}`, { dedication, dedicationText });
      onUpdated({ dedication: updated.dedication, dedicationText: updated.dedicationText });
      setDedication(updated.dedication || '');
      setDedicationText(updated.dedicationText || '');
      setSavedText(true);
    } catch (e) {
      setTextError(e.message || 'فشل حفظ نص الإهداء');
    } finally {
      setSavingText(false);
    }
  };

  // Upload (or replace) the dedication portrait. Stored on the story's
  // characterRefUrl; mainly for manual stories, which have no generated one.
  const uploadPortrait = async (file) => {
    if (!file) return;
    setError(''); setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await api.postForm(`/api/admin/stories/${story._id}/portrait/upload`, fd);
      onUpdated({ characterRefUrl: res.characterRefUrl });
    } catch (e) {
      setError(e.message || 'فشل رفع الصورة');
    } finally {
      setUploading(false);
    }
  };

  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

  const onPointerDown = (e) => {
    if (!portraitUrl) return;
    e.preventDefault();
    drag.current = { x: e.clientX, y: e.clientY };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    const w = frameRef.current?.clientWidth || 240;
    const h = frameRef.current?.clientHeight || 240;
    const dx = e.clientX - drag.current.x;
    const dy = e.clientY - drag.current.y;
    drag.current = { x: e.clientX, y: e.clientY };
    // Dragging the photo right reveals more of its left edge → object-position → 0.
    setPosX((p) => clamp(p - (dx / w) * 100 / zoom, 0, 100));
    setPosY((p) => clamp(p - (dy / h) * 100 / zoom, 0, 100));
    setSaved(false);
  };
  const onPointerUp = (e) => {
    drag.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  const reset = () => { setPosX(50); setPosY(50); setZoom(1); setSaved(false); };
  const dirty = posX !== init(initial.posX, 50) || posY !== init(initial.posY, 50) || zoom !== init(initial.zoom, 1);

  const save = async () => {
    setError(''); setSaving(true);
    try {
      const updated = await api.patch(`/api/admin/stories/${story._id}`, {
        dedicationImageTransform: { posX, posY, zoom },
      });
      onUpdated({ dedicationImageTransform: updated.dedicationImageTransform });
      setSaved(true);
    } catch (e) {
      setError(e.message || 'فشل الحفظ');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-scene-card">
      <div className="admin-scene-media">
        {portraitUrl ? (
          <div
            ref={frameRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
            style={{
              width: 240, height: 240, maxWidth: '100%', borderRadius: '50%', overflow: 'hidden',
              border: '4px solid rgba(253,251,247,0.95)', boxShadow: '0 6px 22px rgba(60,45,25,0.22)',
              cursor: 'grab', touchAction: 'none', background: '#eee',
            }}
          >
            <img
              src={resolveAsset(portraitUrl)}
              alt=""
              draggable={false}
              style={{
                width: '100%', height: '100%', objectFit: 'cover', display: 'block',
                objectPosition: `${posX}% ${posY}%`,
                transform: `scale(${zoom})`,
                transformOrigin: `${posX}% ${posY}%`,
              }}
            />
          </div>
        ) : (
          <p className="admin-scene-meta">لا توجد صورة شخصية لهذه القصة بعد.</p>
        )}
        <span className="admin-scene-meta">صورة صفحة الإهداء</span>
      </div>

      <div className="admin-scene-fields">
        <label className="admin-field-label">صفحة الإهداء — صورة البطل</label>
        <p className="admin-scene-meta">
          ارفع صورة الطفل التي تظهر في دائرة صفحة الإهداء.
        </p>
        <label className="ghost-btn" style={{ display: 'inline-block', cursor: uploading ? 'default' : 'pointer', marginBottom: 12 }}>
          <input type="file" accept="image/*" style={{ display: 'none' }} disabled={uploading}
            onChange={(e) => { uploadPortrait(e.target.files?.[0]); e.target.value = ''; }} />
          {uploading ? 'جارٍ الرفع…' : (portraitUrl ? 'استبدال الصورة' : 'رفع صورة من جهازك')}
        </label>

        <label className="admin-field-label">تأطير الصورة</label>
        <p className="admin-scene-meta">
          اسحب الصورة داخل الدائرة لتحريكها، واستخدم الشريط للتكبير — لتظهر بالشكل الصحيح في الكتاب وملف الـ PDF.
        </p>
        {portraitUrl && (
          <>
            <label className="admin-field-label" style={{ marginTop: 8 }}>تكبير ({zoom.toFixed(2)}×)</label>
            <input
              type="range" min="1" max="3" step="0.05" value={zoom}
              onChange={(e) => { setZoom(Number(e.target.value)); setSaved(false); }}
              style={{ width: '100%' }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <button type="button" className="submit-btn" onClick={save} disabled={saving || !dirty}>
                {saving ? 'جارٍ الحفظ…' : 'حفظ التأطير'}
              </button>
              <button
                type="button" onClick={reset} disabled={saving}
                style={{ background: 'none', border: 'none', color: '#6b5a42', cursor: 'pointer', textDecoration: 'underline' }}
              >
                إعادة ضبط
              </button>
              {saved && <span className="admin-scene-meta" style={{ color: '#2e7d32' }}>✓ تم الحفظ</span>}
              {error && <span className="admin-scene-meta" style={{ color: '#c0392b' }}>{error}</span>}
            </div>
          </>
        )}

        <label className="admin-field-label" style={{ marginTop: 20 }}>نص صفحة الإهداء</label>
        <p className="admin-scene-meta">
          عنوان الإهداء (نفس السطر الذي يظهر على الغلاف الخلفي) والفقرة التذكارية أسفله.
        </p>

        <label className="admin-field-label" style={{ marginTop: 8 }}>عنوان الإهداء</label>
        <p className="admin-scene-meta">
          اتركه فارغاً لاستخدام الإهداء التلقائي: «{autoDedication}»
        </p>
        <textarea
          className="admin-scene-textarea"
          value={dedication}
          onChange={(e) => { setDedication(e.target.value); setSavedText(false); }}
          rows={2}
          dir="rtl"
          placeholder={autoDedication}
          disabled={savingText}
        />

        <label className="admin-field-label" style={{ marginTop: 8 }}>الفقرة التذكارية</label>
        <p className="admin-scene-meta">
          اتركها فارغة لاستخدام النص التلقائي.
        </p>
        <textarea
          className="admin-scene-textarea"
          value={dedicationText}
          onChange={(e) => { setDedicationText(e.target.value); setSavedText(false); }}
          rows={5}
          dir="rtl"
          placeholder={autoText}
          disabled={savingText}
        />

        <div className="admin-scene-actions">
          <button type="button" className="submit-btn" onClick={saveText} disabled={savingText || !textDirty}>
            {savingText ? 'جارٍ الحفظ…' : 'حفظ نص الإهداء'}
          </button>
          {savedText && !textDirty && <span className="admin-scene-meta" style={{ color: '#2e7d32' }}>✓ تم الحفظ</span>}
          {textError && <span className="admin-scene-meta" style={{ color: '#c0392b' }}>{textError}</span>}
        </div>
      </div>
    </div>
  );
}

// Back cover ("the back of the story"): an intro summary line + the dedication
// line. Both are editable; an empty value falls back to the auto-derived
// default (summary from the goal, dedication gendered). The left column mirrors
// the real back page so the admin sees exactly what prints. Patched via the
// same story-level endpoint as the cover.
function BackCoverCard({ story, onUpdated }) {
  const personalization = story.personalization || {};
  const autoSummary = buildSummary(personalization);
  const autoDedication = buildDedication(personalization);

  const [summary, setSummary] = useState(story.summary || '');
  const [dedication, setDedication] = useState(story.dedication || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const dirty = summary !== (story.summary || '') || dedication !== (story.dedication || '');
  const effectiveSummary = summary.trim() || autoSummary;
  const effectiveDedication = dedication.trim() || autoDedication;

  const save = async () => {
    setError('');
    setSaving(true);
    try {
      const updated = await api.patch(`/api/admin/stories/${story._id}`, { summary, dedication });
      onUpdated({ summary: updated.summary, dedication: updated.dedication });
      setSummary(updated.summary || '');
      setDedication(updated.dedication || '');
      setSaved(true);
    } catch (e) {
      setError(e.message || 'فشل حفظ الغلاف الخلفي');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-scene-card">
      <div className="admin-scene-media">
        {story.coverBackImageUrl ? (
          <div className="admin-back-preview page page-back page-back--art">
            <img className="back-cover-image" src={story.coverBackImageUrl} alt="" />
            <div className="back-box">
              <p className="back-summary">{effectiveSummary}</p>
              <div className="back-divider" aria-hidden="true" />
              <p className="back-dedication">{effectiveDedication}</p>
            </div>
            <div className="back-brand">
              <img className="back-logo" src="/logo.png" alt="أنا البطل" />
              <div className="back-site">anabatal.com</div>
            </div>
          </div>
        ) : (
          <div className="admin-back-preview page page-back">
            <p className="back-summary">{effectiveSummary}</p>
            <div className="back-divider" aria-hidden="true" />
            <p className="back-dedication">{effectiveDedication}</p>
          </div>
        )}
        <span className="admin-scene-meta">الغلاف الخلفي</span>
      </div>

      <div className="admin-scene-fields">
        <label className="admin-field-label">النص التعريفي (أعلى الغلاف الخلفي)</label>
        <p className="admin-scene-meta">
          اتركه فارغاً لاستخدام النص التلقائي: «{autoSummary}»
        </p>
        <textarea
          className="admin-scene-textarea"
          value={summary}
          onChange={(e) => { setSummary(e.target.value); setSaved(false); }}
          rows={2}
          dir="rtl"
          placeholder={autoSummary}
          disabled={saving}
        />

        <label className="admin-field-label">الإهداء (أسفل الغلاف الخلفي)</label>
        <p className="admin-scene-meta">
          اتركه فارغاً لاستخدام الإهداء التلقائي: «{autoDedication}»
        </p>
        <textarea
          className="admin-scene-textarea"
          value={dedication}
          onChange={(e) => { setDedication(e.target.value); setSaved(false); }}
          rows={2}
          dir="rtl"
          placeholder={autoDedication}
          disabled={saving}
        />

        {error && <p className="auth-error">{error}</p>}

        <div className="admin-scene-actions">
          <button
            type="button"
            className="submit-btn"
            onClick={save}
            disabled={saving || !dirty}
          >
            {saving ? 'جارٍ الحفظ…' : 'حفظ الغلاف الخلفي'}
          </button>
          {saved && !dirty && <span className="admin-scene-meta">تم الحفظ ✓</span>}
        </div>
      </div>
    </div>
  );
}

// Hero-details card for a manually-built story: the child the book is ABOUT
// (name / gender / age) — distinct from the customer contact on the order.
// `name` + `gender` feed the auto back-cover dedication/summary (bookPdf
// buildDedication/buildSummary) and the displayed hero name; age is
// informational. Patched via the same story-level endpoint as the cover.
const HERO_GENDERS = ['ولد', 'بنت'];

function HeroDetailsCard({ story, onUpdated }) {
  const p = story.personalization || {};
  const initGender = HERO_GENDERS.includes(p.gender) ? p.gender : '';
  const initAge = p.age != null ? String(p.age) : '';
  const [name, setName] = useState(p.name || '');
  const [gender, setGender] = useState(initGender);
  const [age, setAge] = useState(initAge);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const dirty = name !== (p.name || '') || gender !== initGender || age !== initAge;

  const save = async () => {
    setError('');
    setSaving(true);
    try {
      const updated = await api.patch(`/api/admin/stories/${story._id}`, {
        personalization: { name: name.trim(), gender, age: age.trim() },
      });
      onUpdated({ personalization: updated.personalization });
      setSaved(true);
    } catch (e) {
      setError(e.message || 'فشل حفظ تفاصيل البطل');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-scene-card" style={{ display: 'block', marginBottom: 18 }}>
      <h2 className="page-title" style={{ fontSize: '1.05rem', margin: '0 0 4px' }}>تفاصيل البطل</h2>
      <p className="admin-scene-meta">اسم البطل وجنسه يظهران في الإهداء والنص التعريفي على الغلاف الخلفي.</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginTop: 12 }}>
        <div>
          <label className="admin-field-label">اسم البطل</label>
          <input className="admin-title-input" type="text" dir="rtl" value={name}
            onChange={(e) => { setName(e.target.value); setSaved(false); }} placeholder="اسم الطفل في القصة" />
        </div>
        <div>
          <label className="admin-field-label">الجنس</label>
          <select className="admin-title-input" value={gender}
            onChange={(e) => { setGender(e.target.value); setSaved(false); }}>
            <option value="">— غير محدّد —</option>
            <option value="ولد">ولد</option>
            <option value="بنت">بنت</option>
          </select>
        </div>
        <div>
          <label className="admin-field-label">العمر</label>
          <input className="admin-title-input" type="text" dir="ltr" value={age}
            onChange={(e) => { setAge(e.target.value); setSaved(false); }} placeholder="مثال: 4" />
        </div>
      </div>
      {error && <p className="auth-error">{error}</p>}
      <div className="admin-scene-actions" style={{ marginTop: 12 }}>
        <button type="button" className="submit-btn" onClick={save} disabled={saving || !dirty}>
          {saving ? 'جارٍ الحفظ…' : 'حفظ تفاصيل البطل'}
        </button>
        {saved && !dirty && <span className="admin-scene-meta">تم الحفظ ✓</span>}
      </div>
    </div>
  );
}

// Order-details card for a manually-built story: edit the linked admin_manual
// order's customer contact, product type, price, quantity, and payment status so
// the off-platform sale stays tracked. PDF download is gated on the order being
// paid (same rule as storefront orders), so a "view book" link appears once it is.
const MANUAL_PRODUCT_TYPES = [
  { value: 'softcover', label: 'غلاف ورقي' },
  { value: 'hardcover', label: 'غلاف مقوّى' },
  { value: 'digital', label: 'نسخة رقمية (PDF)' },
];

function ManualOrderCard({ storyId, shareToken, barcode }) {
  const [order, setOrder] = useState(null);
  const [contact, setContact] = useState({ name: '', email: '', phone: '' });
  const [productType, setProductType] = useState('softcover');
  const [price, setPrice] = useState('');
  const [qty, setQty] = useState(1);
  const [status, setStatus] = useState('pending_payment');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [printBusy, setPrintBusy] = useState('');
  const [bookBusy, setBookBusy] = useState(false);

  const downloadBook = async () => {
    if (bookBusy || !shareToken) return;
    setBookBusy(true);
    try {
      await downloadBookPdf(shareToken, order?.items?.[0]?.titleAr || 'book', barcode);
    } catch (err) {
      window.alert('فشل تنزيل الكتاب PDF: ' + (err?.message || 'خطأ غير معروف'));
    } finally {
      setBookBusy(false);
    }
  };

  const downloadPrint = async (part) => {
    if (printBusy || !shareToken) return;
    setPrintBusy(part);
    try {
      await downloadPrintPart(shareToken, order?.items?.[0]?.titleAr || 'book', part, barcode);
    } catch (err) {
      window.alert('فشل إنشاء PDF للطباعة: ' + (err?.message || 'خطأ غير معروف'));
    } finally {
      setPrintBusy('');
    }
  };

  const hydrate = (o) => {
    const item = o.items?.[0] || {};
    setOrder(o);
    setContact({ name: o.contact?.name || '', email: o.contact?.email || '', phone: o.contact?.phone || '' });
    setProductType(item.type || 'softcover');
    setPrice(item.priceCents != null ? String((item.priceCents / 100)) : '');
    setQty(item.qty || 1);
    setStatus(o.status || 'pending_payment');
  };

  useEffect(() => {
    api.get(`/api/admin/stories/${storyId}/order`).then(hydrate).catch(() => setOrder(null));
  }, [storyId]);

  const save = async () => {
    setError('');
    setSaving(true);
    setSaved(false);
    try {
      const updated = await api.patch(`/api/admin/orders/${order._id}`, {
        contact,
        productType,
        priceCents: Math.round((Number(price) || 0) * 100),
        qty: Number(qty) || 1,
        status,
      });
      hydrate(updated);
      setSaved(true);
    } catch (e) {
      setError(e.message || 'فشل حفظ الطلب');
    } finally {
      setSaving(false);
    }
  };

  if (!order) return null;

  return (
    <div className="admin-scene-card" style={{ display: 'block', marginBottom: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <h2 className="page-title" style={{ fontSize: '1.05rem', margin: 0 }}>تفاصيل الطلب — {order.orderNumber}</h2>
        <Link to="/admin/orders" className="admin-link" style={{ color: '#6366f1' }}>كل الطلبات</Link>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginTop: 12 }}>
        <div>
          <label className="admin-field-label">اسم العميل</label>
          <input className="admin-title-input" type="text" dir="rtl" value={contact.name}
            onChange={(e) => { setContact({ ...contact, name: e.target.value }); setSaved(false); }} />
        </div>
        <div>
          <label className="admin-field-label">البريد الإلكتروني</label>
          <input className="admin-title-input" type="email" dir="ltr" value={contact.email}
            onChange={(e) => { setContact({ ...contact, email: e.target.value }); setSaved(false); }} />
        </div>
        <div>
          <label className="admin-field-label">الهاتف</label>
          <input className="admin-title-input" type="tel" dir="ltr" value={contact.phone}
            onChange={(e) => { setContact({ ...contact, phone: e.target.value }); setSaved(false); }} />
        </div>
        <div>
          <label className="admin-field-label">نوع المنتج</label>
          <select className="admin-title-input" value={productType} onChange={(e) => { setProductType(e.target.value); setSaved(false); }}>
            {MANUAL_PRODUCT_TYPES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
        <div>
          <label className="admin-field-label">السعر (₪)</label>
          <input className="admin-title-input" type="number" min="0" step="1" dir="ltr" value={price}
            onChange={(e) => { setPrice(e.target.value); setSaved(false); }} />
        </div>
        <div>
          <label className="admin-field-label">الكمية</label>
          <input className="admin-title-input" type="number" min="1" step="1" dir="ltr" value={qty}
            onChange={(e) => { setQty(e.target.value); setSaved(false); }} />
        </div>
        <div>
          <label className="admin-field-label">حالة الطلب</label>
          <select className="admin-title-input" value={status} onChange={(e) => { setStatus(e.target.value); setSaved(false); }}>
            <option value="pending_payment">غير مدفوع</option>
            <option value="paid">مدفوع</option>
            <option value="in_production">قيد الإنتاج</option>
            <option value="shipped">تم الشحن</option>
            <option value="delivered">تم التسليم</option>
            <option value="cancelled">ملغى</option>
          </select>
        </div>
      </div>

      {error && <p className="auth-error">{error}</p>}

      <div className="admin-scene-actions" style={{ marginTop: 12 }}>
        <button type="button" className="submit-btn" onClick={save} disabled={saving}>
          {saving ? 'جارٍ الحفظ…' : 'حفظ تفاصيل الطلب'}
        </button>
        {saved && <span className="admin-scene-meta">تم الحفظ ✓</span>}
        {/* PDF downloads (book + print-house files). This card is admin-only and
            the server lets an admin bypass the payment gate via their Bearer
            token, so the admin can always proof the book — even an unpaid preview
            build that isn't fully illustrated (missing pages render as locked
            placeholders). A non-admin still can't reach the endpoints unpaid. */}
        {shareToken && (
          <>
            <button type="button" className="ghost-btn" onClick={downloadBook} disabled={bookBusy}>
              {bookBusy ? 'جارٍ تجهيز PDF…' : 'تنزيل الكتاب PDF'}
            </button>
            {/* Print house: a button each — the cover wrap, and the interior
                page-by-page (text→image, 2mm bleed), dedication first. */}
            <button type="button" className="ghost-btn" onClick={() => downloadPrint('cover')} disabled={!!printBusy}>
              {printBusy === 'cover' ? 'جارٍ تجهيز PDF…' : 'PDF الغلاف'}
            </button>
            <button type="button" className="ghost-btn" onClick={() => downloadPrint('pages')} disabled={!!printBusy}>
              {printBusy === 'pages' ? 'جارٍ تجهيز PDF…' : 'PDF المحتوى (صفحة صفحة)'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// Story-level imagery style (cartoon | 3d | realistic) — how every illustration
// is rendered. The style is stored on personalization.imageStyle and read by the
// scene/cover regenerate builders. Changing it here persists the new style, then
// offers a one-click "recreate every existing image with this style" pass that
// loops the same per-scene + cover regenerate endpoints (each reads the saved
// style). Only images that ALREADY exist are recreated — un-illustrated scenes
// are left untouched.
const IMAGE_STYLES = [
  { value: 'pixar', label: 'ديزني 3D (بيكسار)' },
  { value: 'anime', label: 'أنمي' },
  { value: 'cartoon', label: 'كرتون' },
  { value: '3d', label: 'ثلاثي الأبعاد' },
  { value: 'realistic', label: 'واقعي' },
];

function ImageStyleCard({ story, onSceneUpdated, onCoverUpdated }) {
  const savedStyle = IMAGE_STYLES.some((s) => s.value === story.personalization?.imageStyle)
    ? story.personalization.imageStyle
    : 'cartoon';
  const [style, setStyle] = useState(savedStyle);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Bulk-recreate progress: { done, total } while running, null when idle.
  const [progress, setProgress] = useState(null);

  // Re-sync the local pick if the saved style changes underneath us (e.g. after
  // the parent refreshes the story).
  useEffect(() => { setStyle(savedStyle); }, [savedStyle]);

  const dirty = style !== savedStyle;
  const busy = saving || progress != null;
  const savedLabel = IMAGE_STYLES.find((s) => s.value === savedStyle)?.label || savedStyle;

  // Existing images a style change can recreate: the cover (if rendered) and every
  // scene that already has an image AND a prompt to regenerate from.
  const recreatable = () => {
    const scenes = (story.scenes || []).filter((s) => s.imageUrl && (s.imagePrompt || '').trim());
    return { scenes, total: scenes.length + (story.coverImageUrl ? 1 : 0) };
  };

  // Recreate every existing image in the now-saved style, sequentially so we don't
  // hammer the image provider. MUST run after the style is persisted — each call
  // re-reads personalization.imageStyle from the DB.
  const recreateAll = async () => {
    const { scenes, total } = recreatable();
    if (total === 0) return;
    setProgress({ done: 0, total });
    let done = 0;
    try {
      if (story.coverImageUrl) {
        const res = await api.post(`/api/admin/stories/${story._id}/cover/regenerate-image`, {});
        onCoverUpdated({
          coverImageUrl: res.coverImageUrl,
          coverBackImageUrl: res.coverBackImageUrl,
          coverSpreadImageUrl: res.coverSpreadImageUrl,
          coverImagePrompt: res.coverImagePrompt,
          coverTitleTheme: res.coverTitleTheme,
        });
        setProgress({ done: ++done, total });
      }
      for (const scene of scenes) {
        const res = await api.post(
          `/api/admin/stories/${story._id}/scenes/${scene.index}/regenerate-image`,
          { imagePrompt: scene.imagePrompt },
        );
        onSceneUpdated(scene.index, res.scene);
        setProgress({ done: ++done, total });
      }
    } catch (e) {
      setError(e.message || 'فشل إعادة توليد بعض الصور');
    } finally {
      setProgress(null);
    }
  };

  const saveStyle = async () => {
    setError('');
    setSaving(true);
    try {
      const updated = await api.patch(`/api/admin/stories/${story._id}`, {
        personalization: { imageStyle: style },
      });
      onCoverUpdated({ personalization: updated.personalization });
      // Offer to recreate every existing image in the new style.
      const { total } = recreatable();
      if (total > 0) {
        const label = IMAGE_STYLES.find((s) => s.value === style)?.label || style;
        const ok = window.confirm(
          `تم تغيير نمط الرسومات إلى «${label}». هل تريد إعادة توليد كل الصور الحالية (الغلاف وكل المشاهد، ${total} صورة) بهذا النمط؟`,
        );
        if (ok) await recreateAll();
      }
    } catch (e) {
      setError(e.message || 'فشل حفظ نمط الرسومات');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-scene-card" style={{ display: 'block', marginBottom: 18 }}>
      <h2 className="page-title" style={{ fontSize: '1.05rem', margin: '0 0 4px' }}>نمط الرسومات</h2>
      <p className="admin-scene-meta">
        طريقة رسم كل صور القصة. عند تغييره وحفظه يمكنك إعادة توليد كل الصور الموجودة بالنمط الجديد بضغطة واحدة.
      </p>
      <div className="admin-cover-theme-actions" style={{ marginTop: 12 }}>
        {IMAGE_STYLES.map((s) => (
          <button
            key={s.value}
            type="button"
            className={style === s.value ? 'submit-btn' : 'ghost-btn'}
            onClick={() => setStyle(s.value)}
            disabled={busy}
          >
            {s.label}
          </button>
        ))}
      </div>
      {error && <p className="auth-error">{error}</p>}
      <div className="admin-scene-actions" style={{ marginTop: 12 }}>
        <button type="button" className="submit-btn" onClick={saveStyle} disabled={busy || !dirty}>
          {saving ? 'جارٍ الحفظ…' : 'حفظ النمط'}
        </button>
        {progress
          ? <span className="admin-scene-meta">جارٍ إعادة توليد الصور… {progress.done}/{progress.total}</span>
          : !dirty && <span className="admin-scene-meta">النمط الحالي: {savedLabel}</span>}
      </div>
    </div>
  );
}

// Per-story body-font override. The global font (Admin → Settings) applies to
// every book; this card lets ONE story print in a different face. Saving updates
// the editor previews immediately (the page sets --story-font from story.storyFont)
// and every later PDF/preview render of this story.
function StoryFontCard({ story, onUpdated }) {
  const saved = story.storyFont || '';
  const [font, setFont] = useState(saved);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const dirty = font !== saved;

  const save = async () => {
    setError('');
    setSaving(true);
    try {
      // '' clears the override → the story follows the global setting again.
      const updated = await api.patch(`/api/admin/stories/${story._id}`, { storyFont: font || null });
      onUpdated({ storyFont: updated.storyFont || null });
    } catch (e) {
      setError(e.message || 'فشل حفظ خط القصة');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-scene-card" style={{ display: 'block', marginBottom: 18 }}>
      <h2 className="page-title" style={{ fontSize: '1.05rem', margin: '0 0 4px' }}>خط نصّ القصة</h2>
      <p className="admin-scene-meta">
        خطّ نصّ هذه القصة وحدها — في الكتاب المطبوع وفي معاينة العميل معاً. «افتراضي» يعني اتّباع
        الخط العام من إعدادات الأدمن. يسري على الملفات التي تُنتَج بعد الحفظ.
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
        <select
          value={font}
          onChange={(e) => setFont(e.target.value)}
          disabled={saving}
          style={{ fontFamily: storyFontCss(font) || undefined, minWidth: 220 }}
        >
          <option value="">افتراضي (الخط العام)</option>
          {STORY_FONTS.map((f) => (
            <option key={f.key} value={f.key} style={{ fontFamily: f.css }}>{f.label}</option>
          ))}
        </select>
        <button type="button" className="submit-btn" onClick={save} disabled={saving || !dirty}>
          {saving ? 'جارٍ الحفظ…' : 'حفظ الخط'}
        </button>
      </div>
      <p
        dir="rtl"
        style={{
          fontFamily: storyFontCss(font) || 'var(--story-font, "Cairo", sans-serif)',
          fontSize: '1.25rem', lineHeight: 2, margin: '14px 0 0', textAlign: 'center',
          background: '#fffdf7', border: '1px solid #ece3cf', borderRadius: 14, padding: '14px 16px',
        }}
      >
        {(story.scenes || []).find((s) => (s.text || '').trim())?.text?.trim().slice(0, 90)
          || 'في المساء، جلس البطل قرب النافذة ونظر إلى النجوم، ثم ابتسم.'}
      </p>
      {error && <p className="auth-error">{error}</p>}
    </div>
  );
}

// Read-only gallery of the images the CUSTOMER uploaded with their order — the
// main child photo plus any extra reference images (a pet, a friend, a place)
// they attached on the create form (personalization.referenceImageUrls). Purely
// informational: the admin inspects these while editing the cover/scenes. It is
// NOT an upload surface — the per-scene and per-cover reference tools handle
// admin-added refs separately. Self-hides when the customer uploaded nothing
// (e.g. manual/hand-built books, or orders placed after customer refs were
// turned off).
function CustomerReferenceCard({ story }) {
  const p = story.personalization || {};
  const childPhoto = p.characterImageUrl || null;
  const refs = Array.isArray(p.referenceImageUrls) ? p.referenceImageUrls.filter(Boolean) : [];
  const descs = Array.isArray(p.referenceImageDescriptions) ? p.referenceImageDescriptions : [];
  if (!childPhoto && refs.length === 0) return null;
  return (
    <div className="admin-scene-card" style={{ display: 'block', marginBottom: 18 }}>
      <h2 className="page-title" style={{ fontSize: '1.05rem', margin: '0 0 4px' }}>
        صور مرجعية من العميل{refs.length ? ` (${refs.length})` : ''}
      </h2>
      <p className="admin-scene-meta">
        الصور التي رفعها العميل مع الطلب — للاطلاع فقط. اضغط أي صورة لفتحها بالحجم الكامل.
      </p>
      <div className="admin-customer-refs">
        {childPhoto && (
          <a
            className="admin-customer-ref admin-customer-ref--primary"
            href={childPhoto}
            target="_blank"
            rel="noreferrer"
          >
            <img className="admin-customer-ref-img" src={childPhoto} alt="صورة الطفل" loading="lazy" />
            <span className="admin-customer-ref-label">صورة الطفل</span>
          </a>
        )}
        {refs.map((url, i) => {
          const d = String(descs[i] || '').trim();
          return (
            <a
              className="admin-customer-ref"
              href={url}
              target="_blank"
              rel="noreferrer"
              key={`${url}-${i}`}
            >
              <img className="admin-customer-ref-img" src={url} alt={d || `مرجع ${i + 1}`} loading="lazy" />
              <span className="admin-customer-ref-label">{d || `مرجع ${i + 1}`}</span>
            </a>
          );
        })}
      </div>
    </div>
  );
}

export default function AdminStory() {
  const t = useT();
  const { id } = useParams();
  const navigate = useNavigate();
  const [story, setStory] = useState(null);
  // Whether the duplicate dialog is open (a full copy of this book as a new story).
  const [duplicating, setDuplicating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Other-gender clone: { busy, url (new preview when created), error }.
  const [genderRegen, setGenderRegen] = useState({ busy: false, url: '', error: '' });
  const [scriptCopied, setScriptCopied] = useState(false);
  const [previewLinkCopied, setPreviewLinkCopied] = useState(false);
  const [printBusy, setPrintBusy] = useState('');

  // Download one print-house file (part='cover' or 'pages'). fetch → blob → click
  // a hidden <a download> so it actually saves. The endpoint is share-token +
  // paid-order gated, so a story without a paid order alerts with the 403.
  const downloadPrintPdf = async (part) => {
    if (printBusy || !story?.shareToken) return;
    setPrintBusy(part);
    try {
      await downloadPrintPart(story.shareToken, story.title, part, story.barcode);
    } catch (err) {
      window.alert('فشل إنشاء PDF للطباعة: ' + (err?.message || 'خطأ غير معروف'));
    } finally {
      setPrintBusy('');
    }
  };

  // Copy the whole story script (title + every scene's text, in order) to the
  // clipboard so the admin can paste it elsewhere for review/editing.
  const copyScript = async () => {
    const parts = [];
    if (story.title) parts.push(story.title);
    (story.scenes || [])
      .slice()
      .sort((a, b) => a.index - b.index)
      .forEach((s) => {
        if (s.text && s.text.trim()) parts.push(`${s.index + 1}. ${s.text.trim()}`);
      });
    const fullScript = parts.join('\n\n');
    try {
      await navigator.clipboard.writeText(fullScript);
      setScriptCopied(true);
      setTimeout(() => setScriptCopied(false), 2000);
    } catch {
      // Fallback for browsers/contexts without the async clipboard API.
      const ta = document.createElement('textarea');
      ta.value = fullScript;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setScriptCopied(true);
      setTimeout(() => setScriptCopied(false), 2000);
    }
  };

  // Customer-facing preview link (view + pay). Manual books are sent to the
  // customer via this /preview/:shareToken URL — cover + scene 1 show, the rest
  // render locked, and the buyer can self-checkout (Stripe) straight from it.
  const previewLink = story?.shareToken
    ? `${window.location.origin}/preview/${story.shareToken}`
    : '';
  const copyPreviewLink = async () => {
    if (!previewLink) return;
    try {
      await navigator.clipboard.writeText(previewLink);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = previewLink;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setPreviewLinkCopied(true);
    setTimeout(() => setPreviewLinkCopied(false), 2000);
  };

  // Clone this story's answers with the gender flipped and generate a fresh
  // preview (server runs the pipeline in the background — we hand the admin the
  // new preview link, which polls while it renders).
  const regenOtherGender = async () => {
    const p = story.personalization || {};
    const srcIsGirl = /بنت|أنثى/.test(String(p.gender || ''));
    const targetGender = srcIsGirl ? 'ولد' : 'بنت';
    const name = window.prompt(
      `إنشاء نسخة جديدة من القصة ${targetGender === 'بنت' ? 'لبنت' : 'لولد'} — ما اسم البطل${targetGender === 'بنت' ? 'ة' : ''}؟`,
      p.name || ''
    );
    if (name == null) return; // cancelled
    setGenderRegen({ busy: true, url: '', error: '' });
    try {
      const res = await api.post(`/api/admin/stories/${story._id}/regenerate-gender`, {
        gender: targetGender,
        name: name.trim(),
      });
      const url = `/preview/${res.shareToken}`;
      setGenderRegen({ busy: false, url, error: '' });
      window.open(url, '_blank');
    } catch (e) {
      setGenderRegen({ busy: false, url: '', error: e.message || 'فشل إنشاء النسخة' });
    }
  };

  useEffect(() => {
    api.get(`/api/admin/stories/${id}`)
      .then(setStory)
      .catch((e) => setError(e.message || 'تعذّر تحميل القصة'))
      .finally(() => setLoading(false));
  }, [id]);

  // While a recast is repainting this book page by page (duplicate-for-another-
  // child), poll so the new illustrations and the progress counter appear as
  // they land. Stops as soon as the run reports done/failed.
  const recastRunning = story?.recast?.status === 'running';
  useEffect(() => {
    if (!recastRunning) return undefined;
    const timer = setInterval(() => {
      api.get(`/api/admin/stories/${id}`).then(setStory).catch(() => {});
    }, 8000);
    return () => clearInterval(timer);
  }, [recastRunning, id]);

  const onCoverUpdated = (patch) => setStory((prev) => ({ ...prev, ...patch }));

  const onSceneUpdated = (index, updatedScene) => {
    setStory((prev) => ({
      ...prev,
      scenes: prev.scenes.map((s) => (s.index === index ? { ...s, ...updatedScene } : s)),
    }));
  };

  // Replace the whole scene list — used when a layout change is applied to ALL
  // scenes (the "apply to all" button returns the full re-saved list).
  const onAllScenesUpdated = (scenes) => setStory((prev) => ({ ...prev, scenes }));

  // Manual-story scene management. Each endpoint returns the full re-indexed
  // scene list, which we set wholesale so indices stay in sync with the server.
  const [sceneOpBusy, setSceneOpBusy] = useState(false);

  // Add a blank scene. `at` (optional) inserts before that index, pushing the
  // rest down; omitted appends at the end. The new scene can then be filled in
  // manually (text), generated, or have an image uploaded via its SceneCard.
  const addScene = async (at) => {
    setSceneOpBusy(true);
    try {
      const body = Number.isInteger(at) ? { at } : {};
      const res = await api.post(`/api/admin/stories/${story._id}/scenes`, body);
      setStory((prev) => ({ ...prev, scenes: res.scenes }));
    } catch (e) {
      window.alert(e.message || 'فشل إضافة المشهد');
    } finally {
      setSceneOpBusy(false);
    }
  };

  const deleteScene = async (index) => {
    if (!window.confirm(`حذف المشهد ${index + 1}؟`)) return;
    setSceneOpBusy(true);
    try {
      const res = await api.delete(`/api/admin/stories/${story._id}/scenes/${index}`);
      setStory((prev) => ({ ...prev, scenes: res.scenes }));
    } catch (e) {
      window.alert(e.message || 'فشل حذف المشهد');
    } finally {
      setSceneOpBusy(false);
    }
  };

  const moveScene = async (index, dir) => {
    const target = index + dir;
    const scenes = story.scenes || [];
    if (target < 0 || target >= scenes.length) return;
    const order = scenes.map((_, i) => i);
    [order[index], order[target]] = [order[target], order[index]];
    setSceneOpBusy(true);
    try {
      const res = await api.post(`/api/admin/stories/${story._id}/scenes/reorder`, { order });
      setStory((prev) => ({ ...prev, scenes: res.scenes }));
    } catch (e) {
      window.alert(e.message || 'فشل إعادة ترتيب المشاهد');
    } finally {
      setSceneOpBusy(false);
    }
  };

  if (loading) return <div className="loader"><div className="loader-spinner" /></div>;
  if (error) return (
    <div className="error-card">
      <h2>{error}</h2>
      <Link to="/admin/orders" className="submit-btn">رجوع</Link>
    </div>
  );
  if (!story) return null;

  return (
    // Scope this story's font override to the whole editor, so every scene
    // preview below renders in the face this book will actually print in.
    <div
      className="admin-page"
      style={storyFontCss(story.storyFont) ? { '--story-font': storyFontCss(story.storyFont) } : undefined}
    >
      <h1 className="page-title">تعديل المشاهد — {story.title || 'قصة'}</h1>
      <p className="admin-scene-subtitle">
        {story.personalization?.name ? `البطل: ${story.personalization.name} · ` : ''}
        الحالة: {story.generationStatus}
      </p>
      <BackendSummary story={story} />

      {/* Recast progress: this book is a duplicate whose illustrations are being
          repainted with a different child (same composition, new hero). */}
      {story.recast?.status && (
        <div
          style={{
            margin: '2px 0 16px', padding: '10px 14px', borderRadius: 12, fontSize: '0.9rem',
            background: story.recast.status === 'running' ? '#eff6ff' : story.recast.error ? '#fef3c7' : '#ecfdf5',
            border: `1px solid ${story.recast.status === 'running' ? '#bfdbfe' : story.recast.error ? '#fde68a' : '#a7f3d0'}`,
            color: '#374151',
          }}
        >
          {story.recast.status === 'running'
            ? `🎨 ${t.adminRecastRunning} (${story.recast.done || 0}/${story.recast.total || 0})`
            : `✅ ${t.adminRecastDone} (${story.recast.done || 0}/${story.recast.total || 0})`}
          {story.recast.error && (
            <div style={{ marginTop: 4, color: '#92400e' }}>{t.adminRecastPartial}: {story.recast.error}</div>
          )}
        </div>
      )}

      {/* Create Series entry point: spin this finished story off into a batch of
          personalized copies (a class set), or jump to the series it belongs to. */}
      <div style={{ margin: '2px 0 16px' }}>
        {story.seriesId ? (
          <Link to={`/admin/series/${story.seriesId}`} className="ghost-btn"
            style={{ padding: '8px 16px', fontSize: '0.9rem' }}>
            📚 عرض السلسلة
          </Link>
        ) : (
          <Link to={`/admin/series/new?fromStory=${story._id}`} className="ghost-btn"
            style={{ padding: '8px 16px', fontSize: '0.9rem' }}>
            📚 إنشاء سلسلة من هذه القصة
          </Link>
        )}
      </div>

      {/* Customer preview link (view + pay) — for manual books sent to the buyer.
          Cover + scene 1 show, the rest render locked, and the buyer can pay via
          Stripe straight from this link. */}
      {story.isManual && story.shareToken && (
        <div
          style={{
            margin: '6px 0 18px', padding: '12px 14px', borderRadius: 12,
            background: '#eef2ff', border: '1px solid #c7d2fe',
            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          }}
        >
          <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#3730a3' }}>
            رابط المعاينة للعميل (عرض + دفع):
          </span>
          <code
            dir="ltr"
            style={{
              flex: '1 1 260px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
              whiteSpace: 'nowrap', fontSize: '0.82rem', background: '#fff',
              padding: '6px 10px', borderRadius: 8, border: '1px solid #e0e7ff',
            }}
          >
            {previewLink}
          </code>
          <button type="button" className="ghost-btn" onClick={copyPreviewLink}
            style={{ padding: '8px 16px', fontSize: '0.9rem' }}>
            {previewLinkCopied ? 'تم النسخ ✓' : '📋 نسخ الرابط'}
          </button>
          <a className="ghost-btn" href={previewLink} target="_blank" rel="noreferrer"
            style={{ padding: '8px 16px', fontSize: '0.9rem' }}>
            فتح المعاينة ↗
          </a>
        </div>
      )}

      {/* Other-gender clone: regenerate the same story (same template + answers)
          for the opposite gender as a brand-new preview. */}
      <div style={{ margin: '6px 0 18px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button
          type="button"
          className="ghost-btn"
          onClick={copyScript}
          style={{ padding: '8px 20px', fontSize: '0.9rem' }}
        >
          {scriptCopied ? 'تم النسخ ✓' : '📋 نسخ نص القصة كاملاً'}
        </button>
        {/* Duplicate this whole book into a new, independently editable story.
            The dialog asks what the copy is (status / test data / paid). */}
        <button
          type="button"
          className="ghost-btn"
          onClick={() => setDuplicating(true)}
          style={{ padding: '8px 20px', fontSize: '0.9rem' }}
        >
          🧬 {t.adminDuplicateStory}
        </button>
        {/* Print-house PDF: a button each — the cover wrap, and the interior
            page-by-page. The endpoint is paid-order gated, but an admin's Bearer
            token (sent via authHeader in downloadPrintPart) bypasses that, so
            these work even before an order exists. */}
        {story.shareToken && (
          <button
            type="button"
            className="ghost-btn"
            onClick={() => downloadPrintPdf('cover')}
            disabled={!!printBusy}
            style={{ padding: '8px 20px', fontSize: '0.9rem' }}
          >
            {printBusy === 'cover' ? 'جارٍ تجهيز PDF…' : '🖨️ PDF الغلاف'}
          </button>
        )}
        {story.shareToken && (
          <button
            type="button"
            className="ghost-btn"
            onClick={() => downloadPrintPdf('pages')}
            disabled={!!printBusy}
            style={{ padding: '8px 20px', fontSize: '0.9rem' }}
          >
            {printBusy === 'pages' ? 'جارٍ تجهيز PDF…' : '🖨️ PDF المحتوى (صفحة صفحة)'}
          </button>
        )}
        {/* Gender clone relies on the template + personalization, which manual
            (hand-built) stories don't have — hide it for them. */}
        {!story.isManual && (
          <>
            <button
              type="button"
              className="submit-btn"
              onClick={regenOtherGender}
              disabled={genderRegen.busy}
              style={{ padding: '8px 20px', fontSize: '0.9rem' }}
            >
              {genderRegen.busy
                ? 'جارٍ إنشاء النسخة…'
                : `🔁 توليد نسخة ${/بنت|أنثى/.test(String(story.personalization?.gender || '')) ? 'لولد' : 'لبنت'}`}
            </button>
            {genderRegen.url && (
              <a className="admin-link" href={genderRegen.url} target="_blank" rel="noreferrer" style={{ color: '#6366f1' }}>
                فُتحت المعاينة الجديدة في تبويب آخر — اضغط هنا إن لم تُفتح
              </a>
            )}
            {genderRegen.error && <span style={{ color: '#b91c1c', fontSize: '0.85rem' }}>{genderRegen.error}</span>}
          </>
        )}
      </div>

      {story.isManual && <ManualOrderCard storyId={story._id} shareToken={story.shareToken} barcode={story.barcode} />}
      {story.isManual && <HeroDetailsCard story={story} onUpdated={onCoverUpdated} />}

      <ImageStyleCard story={story} onSceneUpdated={onSceneUpdated} onCoverUpdated={onCoverUpdated} />

      <StoryFontCard story={story} onUpdated={onCoverUpdated} />

      <CustomerReferenceCard story={story} />

      <div className="admin-scene-list">
        <CoverCard story={story} onCoverUpdated={onCoverUpdated} />
        {(story.scenes || []).map((scene) => (
          // Key by the scene's stable _id, NOT its array index: inserting/
          // deleting/reordering re-packs `index`, and an index key would make
          // React reuse a row for a different scene — dropping the moved scene's
          // image while keeping stale local text. (Falls back to index only for
          // legacy scenes saved before scenes carried an _id.)
          <React.Fragment key={scene._id || scene.index}>
            {/* Insert a blank scene BEFORE this one. The new scene can then be
                written, generated, or uploaded via its card. Available on any
                story so admins can extend a generated book too. */}
            <div className="admin-scene-insert">
              <button
                type="button"
                className="admin-scene-insert-btn"
                onClick={() => addScene(scene.index)}
                disabled={sceneOpBusy}
                title={`إضافة مشهد جديد قبل المشهد ${scene.index + 1}`}
              >
                {sceneOpBusy ? 'جارٍ…' : `+ إضافة مشهد هنا (قبل ${scene.index + 1})`}
              </button>
            </div>
            <SceneCard
              storyId={story._id}
              scene={scene}
              onUpdated={onSceneUpdated}
              onAllUpdated={onAllScenesUpdated}
              isManual
              textBubble={!!story.textBubble}
              textDir={storyTextDir(story.personalization)}
              onDelete={deleteScene}
              onMove={moveScene}
              canMoveUp={scene.index > 0}
              canMoveDown={scene.index < (story.scenes.length - 1)}
            />
          </React.Fragment>
        ))}
        <div className="admin-scene-card" style={{ justifyContent: 'center' }}>
          <button type="button" className="submit-btn" onClick={() => addScene()} disabled={sceneOpBusy}>
            {sceneOpBusy ? 'جارٍ…' : '+ إضافة مشهد جديد في النهاية'}
          </button>
        </div>
        <DedicationPortraitCard story={story} onUpdated={onCoverUpdated} />
        <BackCoverCard story={story} onUpdated={onCoverUpdated} />
      </div>

      {duplicating && (
        <DuplicateStoryModal
          storyId={story._id}
          label={`${story.personalization?.name ? story.personalization.name + ' — ' : ''}${story.title || 'قصة'}`}
          defaultStatus={story.generationStatus}
          defaultIsTest={story.isTest}
          defaultGender={story.personalization?.gender}
          onClose={() => setDuplicating(false)}
          onDone={(result) => {
            setDuplicating(false);
            // Land straight in the copy's editor — the point of duplicating is
            // to start changing it.
            if (result?.id) navigate(`/admin/stories/${result.id}`);
          }}
        />
      )}
    </div>
  );
}
